/**
 * LYT Communications - Map Tracer Tool
 * Version: 2.0.0
 * Route: #map-tracer
 *
 * Full editing suite for fiber optic construction map tracing.
 * Draw bore segments, place devices, measure distances.
 *
 * Keyboard shortcuts:
 *   1-8  = Tool select (Select, Conduit, Fiber, HH, FP, 1x4, 2x8, Splice)
 *   M    = Measure tool
 *   S    = Toggle snap to devices
 *   L    = Toggle legend
 *   Esc  = Cancel / Deselect
 *   Del  = Delete selected
 *   Ctrl+C = Copy selected | Ctrl+V = Paste | Ctrl+D = Duplicate
 *   Ctrl+Z = Undo | Ctrl+Shift+Z / Ctrl+Y = Redo
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, Polyline, OverlayView, Marker } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBS } from '../config/mapConfig';

import * as pdfjsLib from 'pdfjs-dist';
import { publishTracerData, loadTracerFromSheets, loadProjectsList } from '../services/mapService';
import StyleSettingsPanel, { getDefaultStyles } from '../components/MapTracer/StyleSettingsPanel';
import KeyboardShortcutsModal from '../components/MapTracer/KeyboardShortcutsModal';
import BillingSummaryPanel from '../components/MapTracer/BillingSummaryPanel';
import VersionSnapshotsPanel from '../components/MapTracer/VersionSnapshots';

const PDFJS_VERSION = pdfjsLib.version || '4.8.69';
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.mjs`;

// ============================================================
// CONSTANTS
// ============================================================
const MAP_CONTAINER = { width: '100%', height: '100%' };
const DEFAULT_CENTER = { lat: 30.2270, lng: -93.3880 };
const DEFAULT_ZOOM = 16;
const SAVE_KEY = 'maptracer_autosave';
const COLOR_VERSION = '2026-02-21';  // Bump this date when CONDUIT_SPECS or FIBER_SPECS colors change
const SNAP_THRESHOLD_FT = 30;

// IndexedDB for hidden backups
const IDB_NAME = 'maptracer_db';
const IDB_BACKUP = 'hidden_backups';
function openIDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 2); // v2 adds backup store
    req.onupgradeneeded = (e) => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_BACKUP)) db.createObjectStore(IDB_BACKUP);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
// Hidden backup: keeps last 5 copies in IndexedDB, rotates automatically
async function saveHiddenBackup(segments, devices) {
  try {
    const db = await openIDB();
    const tx = db.transaction(IDB_BACKUP, 'readwrite');
    const store = tx.objectStore(IDB_BACKUP);
    const backup = { segments, devices, savedAt: Date.now() };
    // Rotate: keep slots 0-4, overwrite oldest
    const countReq = store.count();
    countReq.onsuccess = () => {
      const slot = countReq.result >= 5 ? countReq.result % 5 : countReq.result;
      store.put(backup, `backup_${slot}`);
    };
  } catch (e) { /* silent */ }
}
async function loadHiddenBackups() {
  try {
    const db = await openIDB();
    const tx = db.transaction(IDB_BACKUP, 'readonly');
    const store = tx.objectStore(IDB_BACKUP);
    return new Promise((resolve) => {
      const req = store.getAll();
      req.onsuccess = () => {
        const backups = (req.result || []).sort((a, b) => b.savedAt - a.savedAt);
        resolve(backups);
      };
      req.onerror = () => resolve([]);
    });
  } catch (e) { return []; }
}
const DARK_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#1d2c4d' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8892b0' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1d2c4d' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#304a7d' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1f2835' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#98a5be' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c5a99' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e1626' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
];

// Tools
const TOOLS = {
  SELECT: 'select',
  CONDUIT: 'conduit',
  FIBER: 'fiber',
  HANDHOLE: 'handhole',
  FLOWERPOT: 'flowerpot',
  SPLITTER_1x4: 'splitter_1x4',
  SPLITTER_2x8: 'splitter_2x8',
  SPLICE_POINT: 'splice_point',
  SLACK_LOOP: 'slack_loop',
  GROUND_ROD: 'ground_rod',
  MEASURE: 'measure',
};

const TOOL_CONFIG = {
  [TOOLS.SELECT]:       { label: 'Select',       icon: '\u25C7', color: '#fff',    cursor: 'default',   key: '1' },
  [TOOLS.CONDUIT]:      { label: 'Conduit',      icon: '\u2501', color: '#F800F8', cursor: 'crosshair', key: '2' },
  [TOOLS.FIBER]:        { label: 'Fiber',        icon: '\u2500', color: '#FF7F00', cursor: 'crosshair', key: '3' },
  [TOOLS.HANDHOLE]:     { label: 'Handhole',     icon: '\u25A1', color: '#00FF00', cursor: 'crosshair', key: '4' },
  [TOOLS.FLOWERPOT]:    { label: 'Flowerpot',    icon: '\u25CF', color: '#FF0000', cursor: 'crosshair', key: '5' },
  [TOOLS.SPLITTER_1x4]: { label: '1x4 Splitter', icon: '\u22C8', color: '#139A47', cursor: 'crosshair', key: '6' },
  [TOOLS.SPLITTER_2x8]: { label: '1x8 Splitter', icon: '\u22C8', color: '#FF4444', cursor: 'crosshair', key: '7' },
  [TOOLS.SPLICE_POINT]: { label: 'Splice Point', icon: '\u22C8', color: '#FF0000', cursor: 'crosshair', key: '8' },
  [TOOLS.SLACK_LOOP]:   { label: 'Slack Loop',   icon: '\u221E', color: '#888',    cursor: 'crosshair', key: '9' },
  [TOOLS.GROUND_ROD]:   { label: 'Ground Rod',   icon: '\u26A1', color: '#795548', cursor: 'crosshair', key: '0' },
  [TOOLS.MEASURE]:      { label: 'Measure',      icon: '\u2195', color: '#FFD700', cursor: 'crosshair', key: 'M' },
};

const SLACK_TYPES = [
  { value: '20', label: "20' Slack" },
  { value: '30', label: "30' Slack" },
  { value: '50', label: "50' Slack" },
];

// Conduit specs (THICK lines) — colors match Vexus Sulphur 006 design map legend
// Each conduit on the design map shows as alternating colored dash pattern; we use the primary color
const CONDUIT_SPECS = [
  { value: '1x1.25',  label: '1x 1.25"',           color: '#00B050' },  // Green (green+magenta pattern)
  { value: '1x1.25f', label: '1x 1.25" *Feeder',   color: '#FF00FF' },  // Magenta (magenta pattern)
  { value: '2x1.25',  label: '2x 1.25"',           color: '#00B050' },  // Green (green+red pattern)
  { value: '2x1.25f', label: '2x 1.25" *Feeder',   color: '#FF00FF' },  // Magenta (magenta+red pattern)
  { value: '3x1.25f', label: '3x 1.25" *Feeder',   color: '#996633' },  // Brown (brown+red pattern)
  { value: '4x1.25f', label: '4x 1.25" *Feeder',   color: '#663311' },  // Dark brown (dk brown+red)
  { value: '5x1.25f', label: '5x 1.25" *Feeder',   color: '#660000' },  // Maroon (dk red+red pattern)
  { value: '1x2.00',  label: '1x 2.00"',           color: '#0000FF' },  // Blue (blue+dk blue pattern)
  { value: '2x2.00',  label: '2x 2.00"',           color: '#00CCFF' },  // Cyan (cyan+blue pattern)
  { value: '3x2.00',  label: '3x 2.00"',           color: '#66DDDD' },  // Light cyan (lt cyan+cyan)
  { value: 'combo1',  label: '1x2.00" + 1x1.25"', color: '#00B050' },  // Green (green+yellow pattern)
  { value: 'combo2',  label: '2x2.00" + 1x1.25"', color: '#800000' },  // Dark maroon (maroon+red)
  { value: 'combo3',  label: '3x2.00" + 1x1.25"', color: '#8B0000' },  // Dark red (dk red+magenta)
  { value: 'combo4',  label: '2x2.00" + 2x1.25"', color: '#800080' },  // Purple (purple+magenta)
];

// Fiber specs (THIN lines) — colors match Vexus Sulphur 006 design map legend
const FIBER_SPECS = [
  { value: 'co432',  label: 'CO432 F1/Feeder',     color: '#FF6600' },  // Orange
  { value: 'bfo432', label: 'BFO432 F1/Feeder',    color: '#993300' },  // Dark brown/maroon
  { value: 'co288',  label: 'CO288 F1/Feeder',     color: '#FF0000' },  // Red
  { value: 'bfo288', label: 'BFO288 F1/Feeder',    color: '#CC0000' },  // Dark red
  { value: 'bfo48m', label: 'BFO48 Micro /Dist',   color: '#00CC00' },  // Green
  { value: 'bfo24m', label: 'BFO24 Micro /Dist',   color: '#008000' },  // Dark green
  { value: 'bfo48',  label: 'BFO48 /Distribution', color: '#003399' },  // Navy blue
  { value: 'bfo24',  label: 'BFO24 /Distribution', color: '#0000FF' },  // Blue
  { value: 'co48',   label: 'CO48 Distribution',    color: '#0066CC' },  // Medium blue
  { value: 'co24',   label: 'CO24 Distribution',    color: '#6633CC' },  // Blue-purple/indigo
];

const HANDHOLE_TYPES = [
  { value: '15x20x12', label: '15x20x12', unitCode: 'UG20' },
  { value: '17x30x18', label: '17x30x18', unitCode: 'UG17' },
  { value: '24x36x18', label: '24x36x18', unitCode: 'UG18' },
  { value: '24x36x24', label: '24x36x24', unitCode: 'UG11' },
  { value: '30x48x18', label: '30x48x18', unitCode: 'UG19' },
  { value: '30x48x24', label: '30x48x24', unitCode: 'UG27' },
  { value: '30x48x30', label: '30x48x30', unitCode: 'UG10' },
];

// Splice subtypes — shown in SP dropdown. Value maps to device type or subtype.
const SPLICE_TYPES = [
  { value: 'splitter_1x4',  label: '1x4 Splitter',    deviceType: 'splitter_1x4' },
  { value: 'splitter_2x8',  label: '1x8 Splitter',    deviceType: 'splitter_2x8' },
  { value: 'splice_point',  label: 'Splice Location',  deviceType: 'splice_point' },
  { value: 'f1',            label: 'F1 / TYCO-D',     deviceType: 'splice_point' },
  { value: 'ribbon',        label: 'Ribbon Splice',    deviceType: 'splice_point' },
];

// ============================================================
// HELPERS
// ============================================================
let idCounter = 1;
function nextId(prefix) {
  return `${prefix}-${String(idCounter++).padStart(3, '0')}`;
}

function getLineColor(spec, overrides) {
  // Check style overrides first (from Style Settings panel)
  if (overrides) {
    if (overrides.conduitColors?.[spec]) return overrides.conduitColors[spec];
    if (overrides.fiberColors?.[spec]) return overrides.fiberColors[spec];
  }
  const found = CONDUIT_SPECS.find(c => c.value === spec) || FIBER_SPECS.find(c => c.value === spec);
  return found ? found.color : '#F800F8';
}

function distanceFt(p1, p2) {
  const R = 6371000;
  const dLat = (p2.lat - p1.lat) * Math.PI / 180;
  const dLng = (p2.lng - p1.lng) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(p1.lat * Math.PI / 180) * Math.cos(p2.lat * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  const m = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(m * 3.28084);
}

function polylineLength(points) {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += distanceFt(points[i - 1], points[i]);
  }
  return total;
}

function getHandholeSnapPoints(d, zoom, iconScale) {
  const size = getMarkerSize(zoom) * iconScale;
  const halfW = (size * 1.5) / 2;   // matches HandholeMarker w = size * 1.5
  const halfH = (size * 1.3) / 2;   // matches HandholeMarker h = size * 1.3
  // Approximate degrees per pixel at this zoom
  const metersPerPx = 0.597 * Math.pow(2, 18 - zoom);
  const latPerPx = metersPerPx / 111111;
  const lngPerPx = metersPerPx / (111111 * Math.cos(d.lat * Math.PI / 180));
  return [
    // Corners (0-3)
    { lat: d.lat + halfH * latPerPx, lng: d.lng - halfW * lngPerPx }, // 0: NW
    { lat: d.lat + halfH * latPerPx, lng: d.lng + halfW * lngPerPx }, // 1: NE
    { lat: d.lat - halfH * latPerPx, lng: d.lng - halfW * lngPerPx }, // 2: SW
    { lat: d.lat - halfH * latPerPx, lng: d.lng + halfW * lngPerPx }, // 3: SE
    // Midpoints of sides (4-7)
    { lat: d.lat + halfH * latPerPx, lng: d.lng },                     // 4: N (top center)
    { lat: d.lat - halfH * latPerPx, lng: d.lng },                     // 5: S (bottom center)
    { lat: d.lat, lng: d.lng - halfW * lngPerPx },                     // 6: W (left center)
    { lat: d.lat, lng: d.lng + halfW * lngPerPx },                     // 7: E (right center)
  ];
}

const BOWTIE_TYPES = new Set(['splitter_1x4', 'splitter_2x8', 'splice_point']);

function getBowtieSnapPoints(d, zoom, iconScale) {
  const baseSize = getMarkerSize(zoom) * iconScale;
  // Match actual marker dimensions at rendering scale
  let halfW, halfH;
  if (d.type === 'splitter_2x8') {
    // SplitterMarker: w = size * 1.8, h = size * 1.0 (rendered at size=baseSize)
    halfW = (baseSize * 1.8) / 2;
    halfH = (baseSize * 1.0) / 2;
  } else if (d.type === 'splitter_1x4') {
    // Splitter1x4Marker: s = size * 1.5 (rendered at size=baseSize*0.8), diamond shape
    const s = baseSize * 0.8 * 1.5;
    halfW = s * 0.45;  // diamond extends to ±45% of s
    halfH = s * 0.45;
  } else {
    // SpliceMarker: w = size * 1.6, h = size * 0.65 (rendered at size=baseSize*0.6)
    halfW = (baseSize * 0.6 * 1.6) / 2;
    halfH = (baseSize * 0.6 * 0.65) / 2;
  }
  const metersPerPx = 0.597 * Math.pow(2, 18 - zoom);
  const latPerPx = metersPerPx / 111111;
  const lngPerPx = metersPerPx / (111111 * Math.cos(d.lat * Math.PI / 180));
  return [
    { lat: d.lat, lng: d.lng - halfW * lngPerPx },  // 0: Left edge
    { lat: d.lat, lng: d.lng + halfW * lngPerPx },  // 1: Right edge
    { lat: d.lat + halfH * latPerPx, lng: d.lng },   // 2: Top
    { lat: d.lat - halfH * latPerPx, lng: d.lng },   // 3: Bottom
  ];
}

function nearestCornerIndex(point, corners) {
  let bestIdx = 0;
  let bestDist = distanceFt(point, corners[0]);
  for (let i = 1; i < corners.length; i++) {
    const d = distanceFt(point, corners[i]);
    if (d < bestDist) { bestDist = d; bestIdx = i; }
  }
  return bestIdx;
}

function snapToDevice(point, deviceList, zoom, iconScale) {
  let nearestHH = null, nearestHHDist = Infinity;
  let nearestBowtie = null, nearestBowtieDist = Infinity;
  let nearestOther = null, nearestOtherDist = Infinity;

  for (const d of deviceList) {
    const dist = distanceFt(point, { lat: d.lat, lng: d.lng });
    if (d.type === 'handhole') {
      if (dist < nearestHHDist) { nearestHHDist = dist; nearestHH = d; }
    } else if (BOWTIE_TYPES.has(d.type)) {
      if (dist < nearestBowtieDist) { nearestBowtieDist = dist; nearestBowtie = d; }
    } else {
      if (dist < nearestOtherDist) { nearestOtherDist = dist; nearestOther = d; }
    }
  }

  // Handholes: snap to nearest corner/midpoint
  if (nearestHH) {
    const snapPts = getHandholeSnapPoints(nearestHH, zoom || 18, iconScale || 1);
    const ci = nearestCornerIndex(point, snapPts);
    const snapDist = distanceFt(point, snapPts[ci]);
    if (snapDist <= SNAP_THRESHOLD_FT) {
      return { ...snapPts[ci], _snap: { deviceId: nearestHH.id, cornerIndex: ci } };
    }
  }

  // Bowties (splitters, splice points): snap to left or right lobe center
  if (nearestBowtie && nearestBowtieDist <= SNAP_THRESHOLD_FT) {
    const snapPts = getBowtieSnapPoints(nearestBowtie, zoom || 18, iconScale || 1);
    const ci = nearestCornerIndex(point, snapPts);
    return { ...snapPts[ci], _snap: { deviceId: nearestBowtie.id, cornerIndex: ci } };
  }

  // Other devices: snap to center
  if (nearestOther && nearestOtherDist <= SNAP_THRESHOLD_FT) {
    return { lat: nearestOther.lat, lng: nearestOther.lng, _snap: { deviceId: nearestOther.id, cornerIndex: -1 } };
  }

  return point;
}

// Recalculate snapped endpoints — uses current zoom so lines stay attached to visible marker edges
function resolveSnappedPath(seg, devices, zoom, iconScale) {
  if (!seg.points || seg.points.length < 2) return seg.points;
  const points = [...seg.points];

  // Check first point — devices can be a Map or array
  if (seg.startSnap) {
    const dev = devices instanceof Map ? devices.get(seg.startSnap.deviceId) : devices.find(d => d.id === seg.startSnap.deviceId);
    if (dev) {
      if (dev.type === 'handhole' && seg.startSnap.cornerIndex >= 0) {
        const snapPts = getHandholeSnapPoints(dev, zoom, iconScale);
        points[0] = snapPts[seg.startSnap.cornerIndex];
      } else if (BOWTIE_TYPES.has(dev.type) && seg.startSnap.cornerIndex >= 0) {
        const snapPts = getBowtieSnapPoints(dev, zoom, iconScale);
        points[0] = snapPts[seg.startSnap.cornerIndex];
      } else {
        points[0] = { lat: dev.lat, lng: dev.lng };
      }
    }
  }

  // Check last point
  if (seg.endSnap) {
    const dev = devices instanceof Map ? devices.get(seg.endSnap.deviceId) : devices.find(d => d.id === seg.endSnap.deviceId);
    if (dev) {
      if (dev.type === 'handhole' && seg.endSnap.cornerIndex >= 0) {
        const snapPts = getHandholeSnapPoints(dev, zoom, iconScale);
        points[points.length - 1] = snapPts[seg.endSnap.cornerIndex];
      } else if (BOWTIE_TYPES.has(dev.type) && seg.endSnap.cornerIndex >= 0) {
        const snapPts = getBowtieSnapPoints(dev, zoom, iconScale);
        points[points.length - 1] = snapPts[seg.endSnap.cornerIndex];
      } else {
        points[points.length - 1] = { lat: dev.lat, lng: dev.lng };
      }
    }
  }

  return points;
}

// Find the angle (degrees) of the nearest segment line at a given point
function getNearestLineAngle(point, segmentList) {
  let bestDist = Infinity;
  let bestAngle = 0;
  for (const seg of segmentList) {
    for (let i = 0; i < seg.points.length - 1; i++) {
      const p1 = seg.points[i];
      const p2 = seg.points[i + 1];
      // Distance from point to line midpoint (simple approximation)
      const mid = { lat: (p1.lat + p2.lat) / 2, lng: (p1.lng + p2.lng) / 2 };
      const d = distanceFt(point, mid);
      // Also check distance to each endpoint
      const d1 = distanceFt(point, p1);
      const d2 = distanceFt(point, p2);
      const minD = Math.min(d, d1, d2);
      if (minD < bestDist) {
        bestDist = minD;
        const dx = (p2.lng - p1.lng) * Math.cos(point.lat * Math.PI / 180);
        const dy = p2.lat - p1.lat;
        bestAngle = Math.atan2(dy, dx) * 180 / Math.PI;
      }
    }
  }
  return bestAngle;
}

// Offset a polyline path perpendicular by offsetMeters (positive = right, negative = left)
function offsetPath(points, offsetMeters) {
  if (points.length < 2) return points;
  const result = [];
  for (let i = 0; i < points.length; i++) {
    let dx, dy;
    if (i === 0) {
      dx = points[1].lng - points[0].lng;
      dy = points[1].lat - points[0].lat;
    } else if (i === points.length - 1) {
      dx = points[i].lng - points[i - 1].lng;
      dy = points[i].lat - points[i - 1].lat;
    } else {
      dx = points[i + 1].lng - points[i - 1].lng;
      dy = points[i + 1].lat - points[i - 1].lat;
    }
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) { result.push(points[i]); continue; }
    // Perpendicular to direction (dx_lng, dy_lat): rotate 90° CW → lat component = -dx, lng component = dy
    const latOff = (-dx / len) * offsetMeters / 111111;
    const lngOff = (dy / len) * offsetMeters / (111111 * Math.cos(points[i].lat * Math.PI / 180));
    result.push({ lat: points[i].lat + latOff, lng: points[i].lng + lngOff });
  }
  return result;
}

// ============================================================
// SVG MARKER COMPONENTS (zoom-responsive)
// ============================================================
function getMarkerSize(zoom, base = 14) {
  const scale = Math.pow(1.2, zoom - 18);
  return Math.max(6, Math.min(32, base * scale));
}

function getConduitWeight(zoom) {
  return Math.max(3, Math.min(8, Math.round((zoom - 13) * 1.5 + 3)));
}
function getFiberWeight(zoom) {
  return Math.max(2, Math.min(5, Math.round((zoom - 13) * 0.8 + 2)));
}

function getFiberOffset(zoom, fiberIndex) {
  // Below zoom 17, fibers sit on top of conduit (blend together)
  // At zoom 17, partial separation. At 18+, full separation.
  if (zoom < 17) return 0;
  const mpp = 156543 * 0.866 / Math.pow(2, zoom);
  const conduitHalf = getConduitWeight(zoom) / 2;
  const sign = fiberIndex >= 0 ? 1 : -1;
  const idx = Math.abs(fiberIndex);
  // Ease in: at zoom 17 use 30% offset, at 18+ use full offset
  const ease = zoom < 18 ? 0.3 : 1.0;
  const pixelOffset = sign * (conduitHalf + 8 + idx * 12) * ease;
  return pixelOffset * mpp;
}

// Handhole — Red rounded rectangle with size text inside (matches design map)
function HandholeMarker({ size, selected, subtype }) {
  const w = size * 1.5;
  const h = size * 1.3;
  const r = Math.min(w, h) * 0.25;
  const fillC = selected ? '#FFD700' : '#fff';
  const strokeC = selected ? '#B8860B' : '#FF0000';
  return (
    <div style={{ position: 'absolute', transform: 'translate(-50%, -50%)', pointerEvents: 'auto', cursor: 'pointer' }}>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <rect x={2} y={2} width={w - 4} height={h - 4} rx={r} ry={r}
          fill={fillC} stroke={strokeC} strokeWidth={2.5} />
        {size > 12 && (
          <text x={w / 2} y={h / 2 + 1} textAnchor="middle" dominantBaseline="middle"
            fill="#000" fontSize={Math.max(6, size * 0.24)} fontWeight="bold">
            {subtype || 'HH'}
          </text>
        )}
      </svg>
    </div>
  );
}

// Flowerpot — Solid red circle with white "FP" text
function FlowerpotMarker({ size, selected }) {
  return (
    <div style={{ position: 'absolute', transform: 'translate(-50%, -50%)', pointerEvents: 'auto', cursor: 'pointer' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={size / 2 - 2}
          fill={selected ? '#FFD700' : '#FF0000'}
          stroke={selected ? '#B8860B' : '#AA0000'}
          strokeWidth={1.5} />
        {size > 18 && (
          <text x={size / 2} y={size / 2 + 1} textAnchor="middle" dominantBaseline="middle"
            fill="#fff" fontSize={Math.max(7, size * 0.35)} fontWeight="bold">FP</text>
        )}
      </svg>
    </div>
  );
}

// 1x4 Splitter — Green diamond with two black boxes containing "1x4" (matches design map)
function Splitter1x4Marker({ size, selected }) {
  const s = size * 1.5;
  const cx = s / 2, cy = s / 2;
  const half = s * 0.45;
  const diamond = `M ${cx},${cy - half} L ${cx + half},${cy} L ${cx},${cy + half} L ${cx - half},${cy} Z`;
  const fillC = selected ? '#FFD700' : '#139A47';
  const strokeC = selected ? '#B8860B' : '#0A6B2E';
  const boxW = s * 0.24, boxH = s * 0.28;
  const gap = 1;
  return (
    <div style={{ position: 'absolute', transform: 'translate(-50%, -50%)', pointerEvents: 'auto', cursor: 'pointer' }}>
      <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
        <path d={diamond} fill={fillC} stroke={strokeC} strokeWidth={2} />
        <rect x={cx - boxW - gap} y={cy - boxH / 2} width={boxW} height={boxH} fill="#000" rx={1} />
        <rect x={cx + gap} y={cy - boxH / 2} width={boxW} height={boxH} fill="#000" rx={1} />
        {size > 14 && (<>
          <text x={cx - boxW / 2 - gap} y={cy + 1} textAnchor="middle" dominantBaseline="middle"
            fill="#4CAF50" fontSize={Math.max(5, size * 0.16)} fontWeight="bold">1x4</text>
          <text x={cx + boxW / 2 + gap} y={cy + 1} textAnchor="middle" dominantBaseline="middle"
            fill="#4CAF50" fontSize={Math.max(5, size * 0.16)} fontWeight="bold">1x4</text>
        </>)}
      </svg>
    </div>
  );
}

// 1x8 Splitter — Magenta bowtie with concentric circles at center pinch (matches design map)
function SplitterMarker({ size, selected }) {
  const w = size * 1.8;
  const h = size * 1.0;
  const cx = w / 2, cy = h / 2;
  const fillC = selected ? '#FFD700' : '#FF00FF';
  const strokeC = selected ? '#B8860B' : '#CC00CC';
  const pinch = w * 0.06;
  const curve = h * 0.2;
  const leftPath = `M 0,0 C ${cx * 0.4},${curve} ${cx - pinch},${cy - curve} ${cx},${cy} C ${cx - pinch},${cy + curve} ${cx * 0.4},${h - curve} 0,${h} Z`;
  const rightPath = `M ${w},0 C ${w - cx * 0.4},${curve} ${cx + pinch},${cy - curve} ${cx},${cy} C ${cx + pinch},${cy + curve} ${w - cx * 0.4},${h - curve} ${w},${h} Z`;
  const circR = Math.min(w, h) * 0.14;
  return (
    <div style={{ position: 'absolute', transform: 'translate(-50%, -50%)', pointerEvents: 'auto', cursor: 'pointer' }}>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <path d={leftPath} fill={fillC} stroke={strokeC} strokeWidth={1.5} />
        <path d={rightPath} fill={fillC} stroke={strokeC} strokeWidth={1.5} />
        <circle cx={cx} cy={cy} r={circR * 1.1} fill="#fff" stroke={strokeC} strokeWidth={1} />
        <circle cx={cx} cy={cy} r={circR * 0.7} fill="none" stroke={strokeC} strokeWidth={1} />
        <circle cx={cx} cy={cy} r={circR * 0.3} fill="none" stroke={strokeC} strokeWidth={0.8} />
        {size > 20 && (
          <text x={cx} y={cy + h * 0.45} textAnchor="middle" fill={selected ? '#000' : '#CC00CC'}
            fontSize={Math.max(6, size * 0.18)} fontWeight="bold">1x8</text>
        )}
      </svg>
    </div>
  );
}

// Splice/TYCO-D — Blue rounded pill with white text (matches design map)
function SpliceMarker({ size, selected }) {
  const w = size * 1.6;
  const h = size * 0.65;
  const r = h / 2;
  const fillC = selected ? '#FFD700' : '#2962FF';
  const strokeC = selected ? '#B8860B' : '#1A44CC';
  return (
    <div style={{ position: 'absolute', transform: 'translate(-50%, -50%)', pointerEvents: 'auto', cursor: 'pointer' }}>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <rect x={1} y={1} width={w - 2} height={h - 2} rx={r} ry={r}
          fill={fillC} stroke={strokeC} strokeWidth={1.5} />
        {size > 10 && (
          <text x={w / 2} y={h / 2 + 1} textAnchor="middle" dominantBaseline="middle"
            fill="#fff" fontSize={Math.max(6, size * 0.22)} fontWeight="bold">TYCO-D</text>
        )}
      </svg>
    </div>
  );
}

// Slack Loop — Teardrop/lasso loop (matches design map)
function SlackLoopMarker({ size, selected, subtype, rotation }) {
  const w = size * 1.8;
  const h = size * 1.4;
  const stroke = selected ? '#FFD700' : '#999';
  const fill = selected ? 'rgba(255,215,0,0.2)' : 'none';
  // Teardrop: loop at top, tail going down-right
  const loopCx = w * 0.4, loopCy = h * 0.38;
  const loopR = Math.min(w, h) * 0.26;
  // Tail curves from bottom of loop outward
  const tailPath = `M ${loopCx + loopR * 0.3},${loopCy + loopR * 0.9} Q ${w * 0.75},${h * 0.7} ${w * 0.85},${h * 0.85}`;
  return (
    <div style={{ position: 'absolute', transform: `translate(-50%, -50%) rotate(${rotation || 0}deg)`, pointerEvents: 'auto', cursor: 'pointer' }}>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <circle cx={loopCx} cy={loopCy} r={loopR} fill={fill} stroke={stroke} strokeWidth={2.5} />
        <path d={tailPath} fill="none" stroke={stroke} strokeWidth={2.5} strokeLinecap="round" />
        {size > 14 && (
          <text x={w / 2} y={h - 2} textAnchor="middle" fill={selected ? '#FFD700' : '#bbb'}
            fontSize={Math.max(6, size * 0.22)} fontWeight="bold">
            {subtype || '30'}'
          </text>
        )}
      </svg>
    </div>
  );
}

// Generate SVG data URI for device markers (used by native google.maps.Marker)
function generateDeviceSvg(type, size, selected, subtype, rotation) {
  let svg, w, h;
  if (type === 'handhole') {
    w = size * 1.5; h = size * 1.3;
    const r = Math.min(w, h) * 0.25;
    const fillC = selected ? '#FFD700' : '#fff';
    const strokeC = selected ? '#B8860B' : '#FF0000';
    const label = size > 12 ? `<text x="${w/2}" y="${h/2+1}" text-anchor="middle" dominant-baseline="middle" fill="#000" font-size="${Math.max(6,size*0.24)}" font-weight="bold">${subtype||'HH'}</text>` : '';
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><rect x="2" y="2" width="${w-4}" height="${h-4}" rx="${r}" ry="${r}" fill="${fillC}" stroke="${strokeC}" stroke-width="2.5"/>${label}</svg>`;
  } else if (type === 'flowerpot') {
    w = size; h = size;
    const fillC = selected ? '#FFD700' : '#FF0000';
    const strokeC = selected ? '#B8860B' : '#AA0000';
    const label = size > 18 ? `<text x="${size/2}" y="${size/2+1}" text-anchor="middle" dominant-baseline="middle" fill="#fff" font-size="${Math.max(7,size*0.35)}" font-weight="bold">FP</text>` : '';
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}"><circle cx="${size/2}" cy="${size/2}" r="${size/2-2}" fill="${fillC}" stroke="${strokeC}" stroke-width="1.5"/>${label}</svg>`;
  } else if (type === 'splitter_1x4') {
    const s = size * 1.5; w = s; h = s;
    const cx = s/2, cy = s/2, half = s*0.45;
    const fillC = selected ? '#FFD700' : '#139A47';
    const strokeC = selected ? '#B8860B' : '#0A6B2E';
    const boxW = s*0.24, boxH = s*0.28, gap = 1;
    const labels = size > 14 ? `<text x="${cx-boxW/2-gap}" y="${cy+1}" text-anchor="middle" dominant-baseline="middle" fill="#4CAF50" font-size="${Math.max(5,size*0.16)}" font-weight="bold">1x4</text><text x="${cx+boxW/2+gap}" y="${cy+1}" text-anchor="middle" dominant-baseline="middle" fill="#4CAF50" font-size="${Math.max(5,size*0.16)}" font-weight="bold">1x4</text>` : '';
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}"><path d="M ${cx},${cy-half} L ${cx+half},${cy} L ${cx},${cy+half} L ${cx-half},${cy} Z" fill="${fillC}" stroke="${strokeC}" stroke-width="2"/><rect x="${cx-boxW-gap}" y="${cy-boxH/2}" width="${boxW}" height="${boxH}" fill="#000" rx="1"/><rect x="${cx+gap}" y="${cy-boxH/2}" width="${boxW}" height="${boxH}" fill="#000" rx="1"/>${labels}</svg>`;
  } else if (type === 'splitter_2x8') {
    w = size * 1.8; h = size * 1.0;
    const cx = w/2, cy = h/2;
    const fillC = selected ? '#FFD700' : '#FF00FF';
    const strokeC = selected ? '#B8860B' : '#CC00CC';
    const pinch = w*0.06, curve = h*0.2;
    const circR = Math.min(w,h)*0.14;
    const label = size > 20 ? `<text x="${cx}" y="${cy+h*0.45}" text-anchor="middle" fill="${selected?'#000':'#CC00CC'}" font-size="${Math.max(6,size*0.18)}" font-weight="bold">1x8</text>` : '';
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><path d="M 0,0 C ${cx*0.4},${curve} ${cx-pinch},${cy-curve} ${cx},${cy} C ${cx-pinch},${cy+curve} ${cx*0.4},${h-curve} 0,${h} Z" fill="${fillC}" stroke="${strokeC}" stroke-width="1.5"/><path d="M ${w},0 C ${w-cx*0.4},${curve} ${cx+pinch},${cy-curve} ${cx},${cy} C ${cx+pinch},${cy+curve} ${w-cx*0.4},${h-curve} ${w},${h} Z" fill="${fillC}" stroke="${strokeC}" stroke-width="1.5"/><circle cx="${cx}" cy="${cy}" r="${circR*1.1}" fill="#fff" stroke="${strokeC}" stroke-width="1"/><circle cx="${cx}" cy="${cy}" r="${circR*0.7}" fill="none" stroke="${strokeC}" stroke-width="1"/><circle cx="${cx}" cy="${cy}" r="${circR*0.3}" fill="none" stroke="${strokeC}" stroke-width="0.8"/>${label}</svg>`;
  } else if (type === 'splice_point') {
    w = size * 1.6; h = size * 0.65;
    const r = h/2;
    const fillC = selected ? '#FFD700' : '#2962FF';
    const strokeC = selected ? '#B8860B' : '#1A44CC';
    const label = size > 10 ? `<text x="${w/2}" y="${h/2+1}" text-anchor="middle" dominant-baseline="middle" fill="#fff" font-size="${Math.max(6,size*0.22)}" font-weight="bold">TYCO-D</text>` : '';
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><rect x="1" y="1" width="${w-2}" height="${h-2}" rx="${r}" ry="${r}" fill="${fillC}" stroke="${strokeC}" stroke-width="1.5"/>${label}</svg>`;
  } else if (type === 'slack_loop') {
    w = size * 1.8; h = size * 1.4;
    const stroke = selected ? '#FFD700' : '#999';
    const fill = selected ? 'rgba(255,215,0,0.2)' : 'none';
    const loopCx = w*0.4, loopCy = h*0.38, loopR = Math.min(w,h)*0.26;
    const label = size > 14 ? `<text x="${w/2}" y="${h-2}" text-anchor="middle" fill="${selected?'#FFD700':'#bbb'}" font-size="${Math.max(6,size*0.22)}" font-weight="bold">${subtype||'30'}'</text>` : '';
    const rot = rotation ? `transform="rotate(${rotation} ${w/2} ${h/2})"` : '';
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><g ${rot}><circle cx="${loopCx}" cy="${loopCy}" r="${loopR}" fill="${fill}" stroke="${stroke}" stroke-width="2.5"/><path d="M ${loopCx+loopR*0.3},${loopCy+loopR*0.9} Q ${w*0.75},${h*0.7} ${w*0.85},${h*0.85}" fill="none" stroke="${stroke}" stroke-width="2.5" stroke-linecap="round"/>${label}</g></svg>`;
  } else {
    w = 12; h = 12;
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"><circle cx="6" cy="6" r="5" fill="${selected?'#FFD700':'#888'}" stroke="#fff" stroke-width="1"/></svg>`;
  }
  return { svg, w, h };
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function MapTracerPage({ darkMode, setCurrentPage }) {
  const mapRef = useRef(null);
  const clipboardRef = useRef(null);
  const pendingFinishRef = useRef(false);
  const drawingPointsRef = useRef([]);
  const skipMapClickRef = useRef(false);
  const shiftHeldRef = useRef(false);
  const ctrlHeldRef = useRef(false);
  const toolLockedRef = useRef(false);
  const shiftLockedRef = useRef(false);
  const lastCtrlTapRef = useRef(0);
  const lastShiftTapRef = useRef(0);
  const shiftDragRef = useRef(null);
  const devicesRef = useRef([]);
  const segmentsRef = useRef([]);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const savedStateLoaded = useRef(false);

  // Tool state
  const [activeTool, setActiveTool] = useState(TOOLS.SELECT);
  const [conduitSpec, setConduitSpec] = useState('2x1.25');
  const [fiberSpec, setFiberSpec] = useState('bfo48m');
  const [recentFibers, setRecentFibers] = useState([]);
  const [handholeType, setHandholeType] = useState('30x48x24');
  const [slackType, setSlackType] = useState('30');
  const [spliceSubtype, setSpliceSubtype] = useState('splitter_1x4');

  // Drawing state
  const [drawingPoints, setDrawingPoints] = useState([]);
  const [mousePos, setMousePos] = useState(null);

  // Data state
  const [segments, setSegments] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [multiSelectedIds, setMultiSelectedIds] = useState(new Set());
  const toggleMultiSelect = useCallback((id) => {
    setMultiSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    // Also add current selectedId to multi-set if starting multi-select
    setSelectedId(prev => {
      if (prev && prev !== id) {
        setMultiSelectedIds(s => { const n = new Set(s); n.add(prev); n.add(id); return n; });
        return null;
      }
      return null;
    });
  }, []);

  // Undo / Redo
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // UI state
  const [showPanel, setShowPanel] = useState(true);
  const [mapDarkMode, setMapDarkMode] = useState(false);
  const [mapTypeId, setMapTypeId] = useState(() => {
    try { const v = localStorage.getItem('lyt-map-type'); return v === 'satellite' ? 'hybrid' : (v || 'hybrid'); } catch { return 'hybrid'; }
  });

  // New features
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [showConduit, setShowConduit] = useState(true);
  const [showFiber, setShowFiber] = useState(true);
  const [showDevices, setShowDevices] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [measurePoints, setMeasurePoints] = useState([]);
  const [showLegend, setShowLegend] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const [iconScale, setIconScale] = useState(1.0);

  const [splitSegments, setSplitSegments] = useState(true);
  const [toolLocked, setToolLocked] = useState(false);
  const [shiftActive, setShiftActive] = useState(false);
  const [footagePopup, setFootagePopup] = useState(null); // { segId, position, value }
  const [isPanning, setIsPanning] = useState(false);
  const [mapBounds, setMapBounds] = useState(null);
  const panIdleTimerRef = useRef(null);

  // Publishing & sync
  const [projectId, setProjectId] = useState('SLPH.01.006');
  const [projectsList, setProjectsList] = useState([]);
  const [isPublishing, setIsPublishing] = useState(false);
  const [lastPublished, setLastPublished] = useState(null);
  const [publishResult, setPublishResult] = useState(null);
  const [autoPushEnabled, setAutoPushEnabled] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const changeCounterRef = useRef(0);
  const lastPushedCounterRef = useRef(0);
  const autoPushIntervalRef = useRef(null);

  // Auto-backup to PC
  const [autoBackupEnabled, setAutoBackupEnabled] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);
  const backupFileHandleRef = useRef(null);
  const autoBackupIntervalRef = useRef(null);

  // UI panels
  const [showStyleSettings, setShowStyleSettings] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [showBillingSummary, setShowBillingSummary] = useState(false);
  const [showVersionSnapshots, setShowVersionSnapshots] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showStatusBar, setShowStatusBar] = useState(true);

  // Style overrides — version 2 resets stale saved colors from old mismatched defaults
  const STYLE_VERSION = 2;
  const [styleOverrides, setStyleOverrides] = useState(() => {
    try {
      const saved = localStorage.getItem('maptracer_styles');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed._version === STYLE_VERSION) return parsed;
        // Old version — discard stale colors, use fresh defaults
        localStorage.removeItem('maptracer_styles');
      }
      return { ...getDefaultStyles(), _version: STYLE_VERSION };
    } catch (e) { return { ...getDefaultStyles(), _version: STYLE_VERSION }; }
  });

  // Items list tabs
  const [itemsTab, setItemsTab] = useState('segments');
  const [itemsSearch, setItemsSearch] = useState('');
  const [itemsExpanded, setItemsExpanded] = useState(true);

  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries: GOOGLE_MAPS_LIBS });

  // ---- Auto-save: Load on mount ----
  useEffect(() => {
    (async () => {
      try {
        const saved = localStorage.getItem(SAVE_KEY);
        if (!saved) return;
        const data = JSON.parse(saved);

        // If color definitions changed, clear stale cache so new colors take effect
        if (data.colorVersion !== COLOR_VERSION) {
          localStorage.removeItem(SAVE_KEY);
          return;
        }

        // Restore saved project selection
        if (data.projectId) setProjectId(data.projectId);

        // Restore data
        if (data.segments?.length) setSegments(data.segments);
        if (data.devices?.length) setDevices(data.devices);
        // Restore settings
        if (data.iconScale) setIconScale(data.iconScale);
        if (data.mapTypeId) setMapTypeId(data.mapTypeId);
        if (data.mapDarkMode !== undefined) setMapDarkMode(data.mapDarkMode);
        if (data.showConduit !== undefined) setShowConduit(data.showConduit);
        if (data.showFiber !== undefined) setShowFiber(data.showFiber);
        if (data.showDevices !== undefined) setShowDevices(data.showDevices);
        if (data.showLabels !== undefined) setShowLabels(data.showLabels);

        // Restore map position
        if (data.mapCenter) setMapCenter(data.mapCenter);
        if (data.mapZoom) setZoom(data.mapZoom);

        const maxSeg = data.segments ? Math.max(0, ...data.segments.map(s => parseInt(s.id.split('-')[1]) || 0)) : 0;
        const maxDev = data.devices ? Math.max(0, ...data.devices.map(d => parseInt(d.id.split('-')[1]) || 0)) : 0;
        idCounter = Math.max(maxSeg, maxDev) + 1;
        savedStateLoaded.current = true;
        setAutoSaveStatus(`Restored ${data.segments?.length || 0} segs, ${data.devices?.length || 0} devices`);
        setTimeout(() => setAutoSaveStatus(''), 3000);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  // ---- Auto-save: Save on changes (debounced) ----
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        // Get current map position
        const center = mapRef.current?.getCenter();
        const currentZoom = mapRef.current?.getZoom();
        const saveData = {
          projectId, segments, devices, iconScale,
          mapCenter: center ? { lat: center.lat(), lng: center.lng() } : mapCenter,
          mapZoom: currentZoom || zoom,
          mapTypeId, mapDarkMode,
          showConduit, showFiber, showDevices, showLabels,
          savedAt: Date.now(),
          colorVersion: COLOR_VERSION,
        };
        localStorage.setItem(SAVE_KEY, JSON.stringify(saveData));
        setAutoSaveStatus('Saved');
        setTimeout(() => setAutoSaveStatus(''), 2000);
      } catch (e) { /* ignore */ }
    }, 5000);
    return () => clearTimeout(timer);
  }, [projectId, segments, devices, iconScale, mapTypeId, mapDarkMode, showConduit, showFiber, showDevices, showLabels, mapCenter, zoom]);

  // Silent hidden backup to IndexedDB every 3 min (always runs, no user action needed)
  useEffect(() => {
    const interval = setInterval(() => {
      if (segmentsRef.current.length > 0 || devicesRef.current.length > 0) {
        saveHiddenBackup(segmentsRef.current, devicesRef.current);
      }
    }, 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Keep refs in sync for use in event listeners
  useEffect(() => { devicesRef.current = devices; }, [devices]);
  useEffect(() => { segmentsRef.current = segments; }, [segments]);

  // Load projects list on mount
  useEffect(() => {
    loadProjectsList().then(list => {
      if (list.length > 0) setProjectsList(list);
    }).catch(() => {});
  }, []);



  // Save style overrides
  useEffect(() => {
    localStorage.setItem('maptracer_styles', JSON.stringify(styleOverrides));
  }, [styleOverrides]);

  // Track changes for auto-push
  useEffect(() => {
    changeCounterRef.current += 1;
  }, [segments, devices]);

  // Auto-push interval
  useEffect(() => {
    if (autoPushEnabled) {
      autoPushIntervalRef.current = setInterval(() => {
        if (changeCounterRef.current > lastPushedCounterRef.current && drawingPoints.length === 0 && !isPublishing) {
          handlePublish();
        }
      }, 5 * 60 * 1000); // 5 minutes
    }
    return () => {
      if (autoPushIntervalRef.current) clearInterval(autoPushIntervalRef.current);
    };
  }, [autoPushEnabled, drawingPoints.length, isPublishing]);

  // Auto-backup to PC file (every 3 minutes)
  const startAutoBackup = useCallback(async () => {
    try {
      // Prompt user to pick save location (once per session)
      const handle = await window.showSaveFilePicker({
        suggestedName: 'maptracer_backup.json',
        types: [{ description: 'JSON', accept: { 'application/json': ['.json'] } }],
      });
      backupFileHandleRef.current = handle;
      setAutoBackupEnabled(true);
      // Immediately write first backup
      const segs = segmentsRef.current, devs = devicesRef.current;
      const data = JSON.stringify({ segments: segs, devices: devs, savedAt: Date.now() });
      const writable = await handle.createWritable();
      await writable.write(data);
      await writable.close();
      saveHiddenBackup(segs, devs); // hidden copy in IndexedDB
      setLastBackup(new Date());
      setAutoSaveStatus('Backup saved to PC');
      setTimeout(() => setAutoSaveStatus(''), 3000);
    } catch (e) {
      if (e.name !== 'AbortError') console.error('Backup setup failed:', e);
    }
  }, []);

  useEffect(() => {
    if (autoBackupEnabled && backupFileHandleRef.current) {
      autoBackupIntervalRef.current = setInterval(async () => {
        try {
          const handle = backupFileHandleRef.current;
          if (!handle) return;
          const segs = segmentsRef.current, devs = devicesRef.current;
          const data = JSON.stringify({ segments: segs, devices: devs, savedAt: Date.now() });
          const writable = await handle.createWritable();
          await writable.write(data);
          await writable.close();
          saveHiddenBackup(segs, devs); // hidden copy in IndexedDB
          setLastBackup(new Date());
        } catch (e) { console.error('Auto-backup failed:', e); }
      }, 3 * 60 * 1000); // 3 minutes
    }
    return () => {
      if (autoBackupIntervalRef.current) clearInterval(autoBackupIntervalRef.current);
    };
  }, [autoBackupEnabled]);

  // Global mouseup ends shift-drag
  useEffect(() => {
    const handleMouseUp = () => { shiftDragRef.current = null; };
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  // ---- Map callbacks ----
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    // Restore saved position after mount
    try {
      const saved = localStorage.getItem(SAVE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        if (data.mapCenter) map.setCenter(data.mapCenter);
        if (data.mapZoom) map.setZoom(data.mapZoom);
        // dark mode styles removed — incompatible with pinned Maps API v3.58
      }
    } catch (e) { /* ignore */ }

    // Shift-drag: mousedown on map near a device or segment starts drag
    map.addListener('mousedown', (e) => {
      if (!(shiftHeldRef.current || shiftLockedRef.current) || !e.latLng) return;
      if (shiftDragRef.current) return;
      const pt = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      let best = null, bestDist = Infinity;
      for (const d of devicesRef.current) {
        const dist = distanceFt(pt, d);
        if (dist < bestDist) { bestDist = dist; best = { type: 'device', id: d.id }; }
      }
      for (const s of segmentsRef.current) {
        for (const p of s.points) {
          const dist = distanceFt(pt, p);
          if (dist < bestDist) { bestDist = dist; best = { type: 'segment', id: s.id }; }
        }
      }
      if (best && bestDist <= 50) {
        shiftDragRef.current = { ...best, lastLat: pt.lat, lastLng: pt.lng };
        skipMapClickRef.current = true;
      }
    });
    map.addListener('mouseup', () => { shiftDragRef.current = null; });

    // Track panning state — hide OverlayViews while moving for smooth panning
    map.addListener('dragstart', () => setIsPanning(true));
    map.addListener('idle', () => {
      setIsPanning(false);
      const b = map.getBounds();
      if (b) setMapBounds({ north: b.getNorthEast().lat(), south: b.getSouthWest().lat(), east: b.getNorthEast().lng(), west: b.getSouthWest().lng() });
    });
    // Set initial bounds
    const initBounds = map.getBounds();
    if (initBounds) setMapBounds({ north: initBounds.getNorthEast().lat(), south: initBounds.getSouthWest().lat(), east: initBounds.getNorthEast().lng(), west: initBounds.getSouthWest().lng() });
  }, []);
  const onZoomChanged = useCallback(() => {
    if (mapRef.current) setZoom(mapRef.current.getZoom());
  }, []);

  const toggleMapType = useCallback(() => {
    const newType = mapTypeId === 'hybrid' ? 'roadmap' : 'hybrid';
    setMapTypeId(newType);
    if (mapRef.current) mapRef.current.setMapTypeId(newType);
  }, [mapTypeId]);

  // ---- Publishing handlers ----
  const handlePublish = useCallback(async () => {
    if (isPublishing) return;
    setIsPublishing(true);
    try {
      const result = await publishTracerData(projectId, segments, devices);
      setPublishResult(result);
      setLastPublished(new Date());
      lastPushedCounterRef.current = changeCounterRef.current;
      if (!result.success) {
        alert(`Publish had errors: ${result.errors?.join(', ')}`);
      }
    } catch (err) {
      alert(`Publish failed: ${err.message}`);
    } finally {
      setIsPublishing(false);
    }
  }, [isPublishing, projectId, segments, devices]);

  const syncFromSheets = useCallback(async (opts = {}) => {
    const { silent = false, confirm: needConfirm = false } = opts;
    if (!projectId) {
      console.warn('[syncFromSheets] No projectId set');
      return;
    }
    if (needConfirm && segments.length > 0 && !window.confirm('Syncing from sheets will replace current data. Continue?')) return;
    setIsSyncing(true);
    setAutoSaveStatus('Syncing from Sheets...');
    try {
      console.log(`[syncFromSheets] Starting sync for ${projectId}...`);
      const result = await loadTracerFromSheets(projectId);
      console.log('[syncFromSheets] Result:', result.success, result.error || '', `segs=${(result.segments||[]).length} devs=${(result.devices||[]).length}`);
      if (result.success) {
        // Normalize segments: fiber needs conduitSpec = fiberSpec for getLineColor()
        const segs = (result.segments || []).map(s => ({
          ...s,
          conduitSpec: s.lineType === 'fiber' ? (s.fiberSpec || s.cableType || s.conduitSpec) : s.conduitSpec,
        }));
        // Normalize devices: flatten position.lat/lng to top-level lat/lng
        const devs = (result.devices || []).map(d => ({
          ...d,
          lat: d.lat ?? d.position?.lat ?? 0,
          lng: d.lng ?? d.position?.lng ?? 0,
          subtype: d.subtype || d.subType || d.spliceType || '',
          notes: d.notes || '',
        }));
        // Auto-snap: reconnect segment endpoints to nearby devices
        const AUTO_SNAP_FT = 150;
        const devIdSet = new Set(devs.map(d => d.id));
        const snappedSegs = segs.map(seg => {
          if (!seg.points || seg.points.length < 2) return seg;
          const updated = { ...seg };
          const startValid = seg.startSnap && devIdSet.has(seg.startSnap.deviceId);
          if (!startValid) {
            updated.startSnap = null;
            const startPt = seg.points[0];
            let bestDist = AUTO_SNAP_FT;
            for (const d of devs) {
              const dist = distanceFt(startPt, { lat: d.lat, lng: d.lng });
              if (dist <= bestDist) { bestDist = dist; updated.startSnap = { deviceId: d.id, cornerIndex: -1 }; }
            }
          }
          const endValid = seg.endSnap && devIdSet.has(seg.endSnap.deviceId);
          if (!endValid) {
            updated.endSnap = null;
            const endPt = seg.points[seg.points.length - 1];
            let bestDist = AUTO_SNAP_FT;
            for (const d of devs) {
              const dist = distanceFt(endPt, { lat: d.lat, lng: d.lng });
              if (dist <= bestDist) { bestDist = dist; updated.endSnap = { deviceId: d.id, cornerIndex: -1 }; }
            }
          }
          return updated;
        });
        // Always set data — even if empty — so old project data is replaced
        setSegments(snappedSegs);
        setDevices(devs);
        if (snappedSegs.length > 0 || devs.length > 0) {
          const maxSeg = Math.max(0, ...snappedSegs.map(s => parseInt(String(s.id).split('-')[1]) || 0));
          const maxDev = Math.max(0, ...devs.map(d => parseInt(String(d.id).split('-')[1]) || 0));
          idCounter = Math.max(idCounter, maxSeg, maxDev) + 1;
        }
        setLastSynced(new Date());
        if (!silent) {
          setAutoSaveStatus(`Synced ${snappedSegs.length} segs, ${devs.length} devices from Sheets`);
          setTimeout(() => setAutoSaveStatus(''), 4000);
        }
      } else {
        console.error('[syncFromSheets] Failed:', result.error);
        if (!silent) {
          setAutoSaveStatus(`Sync failed: ${result.error}`);
          setTimeout(() => setAutoSaveStatus(''), 4000);
        }
      }
    } catch (err) {
      console.error('[syncFromSheets] Error:', err);
      if (!silent) {
        setAutoSaveStatus(`Sync error: ${err.message}`);
        setTimeout(() => setAutoSaveStatus(''), 4000);
      }
    } finally {
      setIsSyncing(false);
    }
  }, [projectId, segments.length]);

  // One-time initial sync from Sheets (uses current projectId, not hardcoded)
  const initialSyncDone = useRef(false);
  useEffect(() => {
    if (!initialSyncDone.current) {
      initialSyncDone.current = true;
      syncFromSheets();
    }
  }, [syncFromSheets]); // eslint-disable-line react-hooks/exhaustive-deps

  // When project changes — clear old data and auto-sync new project
  const prevProjectIdRef = useRef(projectId);
  useEffect(() => {
    if (projectId === prevProjectIdRef.current) return; // skip initial mount
    prevProjectIdRef.current = projectId;
    // Clear old project data immediately
    setSegments([]);
    setDevices([]);
    setSelectedId(null);
    setMultiSelectedIds(new Set());
    setDrawingPoints([]);
    // Clear localStorage so old project data can't reload on refresh
    localStorage.removeItem(SAVE_KEY);
    setAutoSaveStatus('Loading project...');
    // Auto-sync new project from Sheets
    syncFromSheets({ silent: false });
  }, [projectId, syncFromSheets]);

  // Alias for backward compat with More menu
  const handleLoadFromSheets = useCallback(() => syncFromSheets({ confirm: true }), [syncFromSheets]);

  const handleImportExtractionJson = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const text = await file.text();
        const data = JSON.parse(text);
        // extraction.json has segments[], structures[], splice_points[]
        const newSegments = (data.segments || []).map((seg, i) => ({
          id: seg.segment_id || `imported-seg-${i}`,
          lineType: seg.cable_type ? 'fiber' : 'conduit',
          points: [
            seg.gps_start ? { lat: seg.gps_start.lat, lng: seg.gps_start.lng } : null,
            seg.gps_end ? { lat: seg.gps_end.lat, lng: seg.gps_end.lng } : null,
          ].filter(Boolean),
          footage: seg.footage || 0,
          street: seg.street_name || seg.description || '',
          conduitSpec: seg.duct_count ? `${seg.duct_count}x1.25` : '2x1.25',
          cableType: seg.cable_type || '',
        }));

        const newDevices = (data.structures || []).map((s, i) => ({
          id: s.id || `imported-dev-${i}`,
          type: s.type || 'handhole',
          lat: s.gps ? s.gps.lat : 0,
          lng: s.gps ? s.gps.lng : 0,
          subtype: s.size || '',
          unitCode: s.unit_code || '',
        }));

        setSegments(prev => [...prev, ...newSegments]);
        setDevices(prev => [...prev, ...newDevices]);
        alert(`Imported ${newSegments.length} segments and ${newDevices.length} devices from extraction`);
      } catch (err) {
        alert(`Import failed: ${err.message}`);
      }
    };
    input.click();
  }, []);

  const handleVersionLoad = useCallback((snapshotData) => {
    if (snapshotData.segments) setSegments(snapshotData.segments);
    if (snapshotData.devices) setDevices(snapshotData.devices);
  }, []);

  const handleImportJson = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (data.segments) setSegments(data.segments.map(s => ({ ...s, points: s.points || [{ lat: s.gps_start_lat, lng: s.gps_start_lng }, { lat: s.gps_end_lat, lng: s.gps_end_lng }] })));
        if (data.devices) setDevices(data.devices);
        if (data.iconScale) setIconScale(data.iconScale);
        const maxSeg = data.segments ? Math.max(0, ...data.segments.map(s => parseInt(s.id.split('-')[1]) || 0)) : 0;
        const maxDev = data.devices ? Math.max(0, ...data.devices.map(d => parseInt(d.id.split('-')[1]) || 0)) : 0;
        idCounter = Math.max(maxSeg, maxDev) + 1;
        setUndoStack([]); setRedoStack([]);
      } catch (err) { alert('Invalid JSON file'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  }, []);

  const handleExportJson = useCallback(() => {
    const data = {
      project: projectId,
      exported: new Date().toISOString(),
      segments: segments.map(s => ({
        id: s.id, lineType: s.lineType, points: s.points, conduitSpec: s.conduitSpec,
        footage: s.footage, street: s.street, notes: s.notes || '',
        gps_start_lat: s.points[0].lat, gps_start_lng: s.points[0].lng,
        gps_end_lat: s.points[s.points.length - 1].lat, gps_end_lng: s.points[s.points.length - 1].lng,
      })),
      devices: devices.map(d => ({
        id: d.id, type: d.type, lat: d.lat, lng: d.lng, subtype: d.subtype, notes: d.notes || '',
      })),
      iconScale,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${projectId}_traced_${Date.now()}.json`; a.click();
    URL.revokeObjectURL(url);
  }, [projectId, segments, devices, iconScale]);

  // ---- Keyboard shortcuts ----
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') return;

      // Shift tracking + double-tap to lock
      if (e.key === 'Shift') {
        if (!shiftHeldRef.current) {
          shiftHeldRef.current = true;
          const now = Date.now();
          if (now - lastShiftTapRef.current < 350) {
            shiftLockedRef.current = !shiftLockedRef.current;
            setShiftActive(shiftLockedRef.current);
          } else {
            setShiftActive(true);
          }
          lastShiftTapRef.current = now;
          if (mapRef.current) mapRef.current.setOptions({ draggable: false });
        }
        return;
      }
      // Ctrl tracking + double-tap to lock tool
      if (e.key === 'Control') {
        if (!ctrlHeldRef.current) {
          ctrlHeldRef.current = true;
          const now = Date.now();
          if (now - lastCtrlTapRef.current < 350) {
            toolLockedRef.current = !toolLockedRef.current;
            setToolLocked(toolLockedRef.current);
          }
          lastCtrlTapRef.current = now;
        }
        return;
      }

      if (e.key === 'Escape') {
        // Escape also clears any locked modes
        if (toolLockedRef.current) { toolLockedRef.current = false; setToolLocked(false); }
        if (shiftLockedRef.current) { shiftLockedRef.current = false; setShiftActive(false); if (mapRef.current) mapRef.current.setOptions({ draggable: true }); }
        if (measurePoints.length > 0) { setMeasurePoints([]); }
        else if (drawingPoints.length > 0) { setDrawingPoints([]); setMousePos(null); }
        else { setSelectedId(null); setMultiSelectedIds(new Set()); setActiveTool(TOOLS.SELECT); }
      }
      if (e.key === 'Delete') {
        if (multiSelectedIds.size > 0) {
          const ids = new Set(multiSelectedIds);
          if (selectedId) ids.add(selectedId);
          setSegments(prev => prev.filter(s => !ids.has(s.id)));
          setDevices(prev => prev.filter(d => !ids.has(d.id)));
          setSelectedId(null);
          setMultiSelectedIds(new Set());
        } else if (selectedId) {
          handleDelete(selectedId);
        }
      }

      // Undo / Redo
      if (e.key === 'z' && (e.ctrlKey || e.metaKey) && !e.shiftKey) { e.preventDefault(); handleUndo(); }
      if ((e.key === 'Z' || e.key === 'z') && (e.ctrlKey || e.metaKey) && e.shiftKey) { e.preventDefault(); handleRedo(); }
      if (e.key === 'y' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleRedo(); }

      // Publish/Export/Import
      if (e.key === 'p' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handlePublish(); }
      if (e.key === 'e' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleExportJson(); }
      if (e.key === 'i' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); document.getElementById('import-json-input')?.click(); }

      // Copy (Ctrl+C)
      if (e.key === 'c' && (e.ctrlKey || e.metaKey) && selectedId) {
        e.preventDefault();
        const seg = segments.find(s => s.id === selectedId);
        const dev = devices.find(d => d.id === selectedId);
        if (seg) clipboardRef.current = { type: 'segment', data: { ...seg } };
        else if (dev) clipboardRef.current = { type: 'device', data: { ...dev } };
      }

      // Paste (Ctrl+V)
      if (e.key === 'v' && (e.ctrlKey || e.metaKey) && clipboardRef.current) {
        e.preventDefault();
        const cb = clipboardRef.current;
        const offset = 0.0003; // ~100ft offset
        if (cb.type === 'device') {
          const newId = nextId(cb.data.type === 'handhole' ? 'HH' : cb.data.type === 'flowerpot' ? 'FP' : cb.data.type === 'splitter_1x4' ? 'TB' : cb.data.type === 'splitter_2x8' ? 'SP' : 'SC');
          const newDev = { ...cb.data, id: newId, lat: cb.data.lat + offset, lng: cb.data.lng + offset };
          setDevices(prev => [...prev, newDev]);
          setSelectedId(newId);
          setUndoStack(prev => [...prev, { action: 'addDevice', id: newId }]);
          setRedoStack([]);
        } else if (cb.type === 'segment') {
          const newId = nextId('SEG');
          const newSeg = { ...cb.data, id: newId, points: cb.data.points.map(p => ({ lat: p.lat + offset, lng: p.lng + offset })) };
          setSegments(prev => [...prev, newSeg]);
          setSelectedId(newId);
          setUndoStack(prev => [...prev, { action: 'addSegment', id: newId }]);
          setRedoStack([]);
        }
      }

      // Duplicate (Ctrl+D)
      if (e.key === 'd' && (e.ctrlKey || e.metaKey) && selectedId) {
        e.preventDefault();
        const seg = segments.find(s => s.id === selectedId);
        const dev = devices.find(d => d.id === selectedId);
        const offset = 0.0003;
        if (dev) {
          const newId = nextId(dev.type === 'handhole' ? 'HH' : dev.type === 'flowerpot' ? 'FP' : dev.type === 'splitter_1x4' ? 'TB' : dev.type === 'splitter_2x8' ? 'SP' : 'SC');
          const newDev = { ...dev, id: newId, lat: dev.lat + offset, lng: dev.lng + offset };
          setDevices(prev => [...prev, newDev]);
          setSelectedId(newId);
          setUndoStack(prev => [...prev, { action: 'addDevice', id: newId }]);
          setRedoStack([]);
        } else if (seg) {
          const newId = nextId('SEG');
          const newSeg = { ...seg, id: newId, points: seg.points.map(p => ({ lat: p.lat + offset, lng: p.lng + offset })) };
          setSegments(prev => [...prev, newSeg]);
          setSelectedId(newId);
          setUndoStack(prev => [...prev, { action: 'addSegment', id: newId }]);
          setRedoStack([]);
        }
      }

      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // Tool shortcuts
      const toolKeys = { '1': TOOLS.SELECT, '2': TOOLS.CONDUIT, '3': TOOLS.FIBER, '4': TOOLS.HANDHOLE, '5': TOOLS.FLOWERPOT, '6': TOOLS.SPLICE_POINT, '7': TOOLS.SLACK_LOOP, '8': TOOLS.GROUND_ROD };
      if (toolKeys[e.key]) {
        setActiveTool(toolKeys[e.key]);
        if (drawingPoints.length >= 2 && (e.key !== '2' && e.key !== '3')) finishSegment();
        if (toolKeys[e.key] !== TOOLS.CONDUIT && toolKeys[e.key] !== TOOLS.FIBER) { setDrawingPoints([]); setMousePos(null); }
      }
      if (e.key === 'm' || e.key === 'M') { setActiveTool(TOOLS.MEASURE); setMeasurePoints([]); setDrawingPoints([]); setMousePos(null); }
      if (e.key === 's') setSnapEnabled(prev => !prev);
      if (e.key === 'l') setShowLegend(prev => !prev);
      if (e.key === 'b') setShowBillingSummary(prev => !prev);
      if (e.key === 't') toggleMapType();
      if (e.key === '?') setShowShortcutsModal(true);
      // J = Join/Merge selected segments
      if (e.key === 'j' && !e.ctrlKey && !e.metaKey) { handleMerge(); }
    };
    const upHandler = (e) => {
      if (e.key === 'Shift') {
        shiftHeldRef.current = false;
        if (!shiftLockedRef.current) {
          setShiftActive(false);
          if (mapRef.current) mapRef.current.setOptions({ draggable: true });
          shiftDragRef.current = null;
        }
      }
      if (e.key === 'Control') {
        ctrlHeldRef.current = false;
      }
    };
    window.addEventListener('keydown', handler);
    window.addEventListener('keyup', upHandler);
    return () => { window.removeEventListener('keydown', handler); window.removeEventListener('keyup', upHandler); };
  });

  // ---- Map click handler ----
  const onMapClick = useCallback((e) => {
    // Dismiss footage popup on map click
    if (footagePopup) { setFootagePopup(null); }
    // Skip if a polyline/shape click or shift-drag already handled this event
    if (skipMapClickRef.current) {
      skipMapClickRef.current = false;
      return;
    }
    // Skip clicks while shift-dragging
    if (shiftHeldRef.current || shiftLockedRef.current) return;

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    const point = { lat, lng };

    // Measure tool
    if (activeTool === TOOLS.MEASURE) {
      setMeasurePoints(prev => prev.length >= 2 ? [point] : [...prev, point]);
      return;
    }

    if (activeTool === TOOLS.CONDUIT || activeTool === TOOLS.FIBER) {
      // Always snap to handholes; snap to other devices only when snapEnabled
      const snapped = snapToDevice(point, snapEnabled ? devices : devices.filter(d => d.type === 'handhole'), zoom, iconScale);
      setDrawingPoints(prev => [...prev, snapped]);
    } else if (activeTool === TOOLS.SELECT) {
      setSelectedId(null);
    } else {
      let deviceType;
      if (activeTool === TOOLS.SPLICE_POINT || activeTool === TOOLS.SPLITTER_1x4 || activeTool === TOOLS.SPLITTER_2x8) {
        // All splice tools consolidated — resolve type from spliceSubtype dropdown
        const spliceEntry = SPLICE_TYPES.find(t => t.value === spliceSubtype);
        deviceType = spliceEntry?.deviceType || 'splice_point';
      } else {
        const typeMap = {
          [TOOLS.HANDHOLE]: 'handhole',
          [TOOLS.FLOWERPOT]: 'flowerpot',
          [TOOLS.SLACK_LOOP]: 'slack_loop',
          [TOOLS.GROUND_ROD]: 'ground_rod',
        };
        deviceType = typeMap[activeTool];
      }
      if (deviceType) {
        const prefixMap = { handhole: 'HH', flowerpot: 'FP', splitter_1x4: 'TB', splitter_2x8: 'SP', splice_point: 'SC', slack_loop: 'SL', ground_rod: 'GR' };
        const device = {
          id: nextId(prefixMap[deviceType] || 'DV'),
          type: deviceType,
          lat, lng,
          subtype: deviceType === 'handhole' ? handholeType : deviceType === 'slack_loop' ? slackType : spliceSubtype,
          rotation: deviceType === 'slack_loop' ? getNearestLineAngle(point, segments) : undefined,
          unitCode: deviceType === 'ground_rod' ? 'UG13'
            : deviceType === 'handhole' ? (HANDHOLE_TYPES.find(h => h.value === handholeType)?.unitCode || 'UG17')
            : deviceType === 'flowerpot' ? 'UG12'
            : undefined,
          notes: '',
        };
        setDevices(prev => [...prev, device]);
        setUndoStack(prev => [...prev, { action: 'addDevice', id: device.id }]);
        setRedoStack([]);
        // Auto-return to Select unless Ctrl held or tool locked
        if (!ctrlHeldRef.current && !toolLockedRef.current) {
          setSelectedId(device.id);
          setActiveTool(TOOLS.SELECT);
        }
      }
    }
  }, [activeTool, conduitSpec, handholeType, slackType, spliceSubtype, snapEnabled, devices, segments, zoom, iconScale]);

  const onMapDblClick = useCallback((e) => {
    if (activeTool === TOOLS.CONDUIT || activeTool === TOOLS.FIBER) {
      // Don't call finishSegment directly — drawingPoints is stale here because
      // the two single-click events from the double-click already queued state updates
      // that haven't been applied yet. Set the flag and let useEffect handle it.
      pendingFinishRef.current = true;
    }
  }, [activeTool]);

  // ---- Delete ----
  const handleDelete = useCallback((id) => {
    const deletedSeg = segments.find(s => s.id === id);
    const deletedDev = devices.find(d => d.id === id);
    if (deletedSeg) {
      setUndoStack(prev => [...prev, { action: 'deleteSegment', segment: deletedSeg }]);
    } else if (deletedDev) {
      setUndoStack(prev => [...prev, { action: 'deleteDevice', device: deletedDev }]);
    }
    setSegments(prev => prev.filter(s => s.id !== id));
    setDevices(prev => prev.filter(d => d.id !== id));
    setSelectedId(null);
    setRedoStack([]);
  }, [segments, devices]);

  const onMapRightClick = useCallback((e) => {
    if (activeTool === TOOLS.MEASURE) { setMeasurePoints([]); return; }
    if ((activeTool === TOOLS.CONDUIT || activeTool === TOOLS.FIBER) && drawingPoints.length > 0) {
      setDrawingPoints(prev => prev.slice(0, -1));
      return;
    }
    // If not drawing, delete selected item
    if (selectedId) {
      handleDelete(selectedId);
    }
  }, [activeTool, drawingPoints, selectedId, handleDelete]);

  const onMouseMove = useCallback((e) => {
    // Shift-drag: move object(s) by delta
    if (shiftDragRef.current && e.latLng) {
      const lat = e.latLng.lat(), lng = e.latLng.lng();
      const drag = shiftDragRef.current;
      const dLat = lat - drag.lastLat, dLng = lng - drag.lastLng;
      drag.lastLat = lat; drag.lastLng = lng;
      if (drag.type === 'multi' && drag.ids) {
        // Move all selected objects at once
        setDevices(prev => prev.map(d => drag.ids.has(d.id) ? { ...d, lat: d.lat + dLat, lng: d.lng + dLng } : d));
        setSegments(prev => prev.map(s => !drag.ids.has(s.id) ? s : {
          ...s, points: s.points.map(p => ({ ...p, lat: p.lat + dLat, lng: p.lng + dLng })),
        }));
      } else if (drag.type === 'device') {
        setDevices(prev => prev.map(d => d.id === drag.id ? { ...d, lat: d.lat + dLat, lng: d.lng + dLng } : d));
      } else if (drag.type === 'segment') {
        setSegments(prev => prev.map(s => s.id !== drag.id ? s : {
          ...s, points: s.points.map(p => ({ ...p, lat: p.lat + dLat, lng: p.lng + dLng })),
        }));
      }
      return;
    }
    if ((activeTool === TOOLS.CONDUIT || activeTool === TOOLS.FIBER) && drawingPoints.length > 0) {
      const raw = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      // Snap preview to handhole snap points
      const snapped = snapToDevice(raw, devicesRef.current, zoom, iconScale);
      setMousePos(snapped._snap ? snapped : raw);
    }
  }, [activeTool, drawingPoints, zoom, iconScale]);

  // ---- Finish segment ----
  const finishSegment = useCallback(() => {
    // Deduplicate consecutive identical points (from double-click adding two clicks)
    const deduped = drawingPoints.filter((pt, i) =>
      i === 0 || pt.lat !== drawingPoints[i - 1].lat || pt.lng !== drawingPoints[i - 1].lng
    );
    if (deduped.length < 2) return;
    const isFiber = activeTool === TOOLS.FIBER;

    // Extract snap metadata from first/last points (set by snapToDevice)
    const startSnap = deduped[0]._snap || null;
    const endSnap = deduped[deduped.length - 1]._snap || null;
    // Clean _snap metadata from all points before storing
    const cleanPoints = deduped.map(({ _snap, ...rest }) => rest);

    let lastSegId;
    if (splitSegments && cleanPoints.length > 2) {
      // Split into individual segments between each pair of consecutive points
      const newSegs = [];
      for (let i = 0; i < cleanPoints.length - 1; i++) {
        const pts = [cleanPoints[i], cleanPoints[i + 1]];
        const seg = {
          id: nextId(isFiber ? 'FBR' : 'SEG'),
          lineType: isFiber ? 'fiber' : 'conduit',
          points: pts,
          conduitSpec: isFiber ? fiberSpec : conduitSpec,
          footage: polylineLength(pts),
          street: '', notes: '',
        };
        // First sub-segment gets startSnap, last gets endSnap
        if (i === 0 && startSnap) seg.startSnap = startSnap;
        if (i === cleanPoints.length - 2 && endSnap) seg.endSnap = endSnap;
        newSegs.push(seg);
      }
      setSegments(prev => [...prev, ...newSegs]);
      setUndoStack(prev => [...prev, ...newSegs.map(s => ({ action: 'addSegment', id: s.id }))]);
      lastSegId = newSegs[newSegs.length - 1].id;
    } else {
      // One continuous segment with all points
      const seg = {
        id: nextId(isFiber ? 'FBR' : 'SEG'),
        lineType: isFiber ? 'fiber' : 'conduit',
        points: [...cleanPoints],
        conduitSpec: isFiber ? fiberSpec : conduitSpec,
        footage: polylineLength(cleanPoints),
        street: '', notes: '',
      };
      if (startSnap) seg.startSnap = startSnap;
      if (endSnap) seg.endSnap = endSnap;
      setSegments(prev => [...prev, seg]);
      setUndoStack(prev => [...prev, { action: 'addSegment', id: seg.id }]);
      lastSegId = seg.id;
    }
    setRedoStack([]);
    setDrawingPoints([]);
    setMousePos(null);
    // Track recent fiber specs
    if (isFiber) {
      setRecentFibers(prev => {
        const filtered = prev.filter(v => v !== fiberSpec);
        return [fiberSpec, ...filtered].slice(0, 5);
      });
    }
    // Show footage popup at segment midpoint
    const mid = {
      lat: (cleanPoints[0].lat + cleanPoints[cleanPoints.length - 1].lat) / 2,
      lng: (cleanPoints[0].lng + cleanPoints[cleanPoints.length - 1].lng) / 2,
    };
    setSelectedId(lastSegId);
    setFootagePopup({ segId: lastSegId, position: mid });
  }, [drawingPoints, conduitSpec, fiberSpec, activeTool, splitSegments]);

  // Keep drawingPointsRef in sync + handle pending finish from device double-click
  useEffect(() => {
    drawingPointsRef.current = drawingPoints;
    if (pendingFinishRef.current && drawingPoints.length >= 2) {
      pendingFinishRef.current = false;
      finishSegment();
    }
  }, [drawingPoints, finishSegment]);

  // ---- Merge selected segments into one line ----
  const handleMerge = useCallback(() => {
    const ids = new Set(multiSelectedIds);
    if (selectedId) ids.add(selectedId);
    // Only merge segments (not devices)
    const segs = segments.filter(s => ids.has(s.id));
    if (segs.length < 2) return;

    const MERGE_DIST = 50; // feet tolerance for matching endpoints
    const used = new Set();
    const chain = [{ ...segs[0], points: [...segs[0].points] }];
    used.add(segs[0].id);

    // Greedily build a chain by matching endpoints
    let changed = true;
    while (changed) {
      changed = false;
      for (const seg of segs) {
        if (used.has(seg.id)) continue;
        const chainEnd = chain[chain.length - 1].points[chain[chain.length - 1].points.length - 1];
        const chainStart = chain[0].points[0];
        const segStart = seg.points[0];
        const segEnd = seg.points[seg.points.length - 1];

        if (distanceFt(chainEnd, segStart) < MERGE_DIST) {
          chain.push({ ...seg, points: [...seg.points] });
          used.add(seg.id); changed = true;
        } else if (distanceFt(chainEnd, segEnd) < MERGE_DIST) {
          chain.push({ ...seg, points: [...seg.points].reverse() });
          used.add(seg.id); changed = true;
        } else if (distanceFt(chainStart, segEnd) < MERGE_DIST) {
          chain.unshift({ ...seg, points: [...seg.points] });
          used.add(seg.id); changed = true;
        } else if (distanceFt(chainStart, segStart) < MERGE_DIST) {
          chain.unshift({ ...seg, points: [...seg.points].reverse() });
          used.add(seg.id); changed = true;
        }
      }
    }
    // Any remaining unlinked segments — append in order
    for (const seg of segs) {
      if (!used.has(seg.id)) { chain.push({ ...seg, points: [...seg.points] }); used.add(seg.id); }
    }

    // Build merged points — skip duplicate junction points
    const mergedPts = [...chain[0].points];
    for (let i = 1; i < chain.length; i++) {
      const pts = chain[i].points;
      const gap = distanceFt(mergedPts[mergedPts.length - 1], pts[0]);
      const startJ = gap < MERGE_DIST ? 1 : 0; // skip first point if it overlaps
      for (let j = startJ; j < pts.length; j++) mergedPts.push(pts[j]);
    }

    const first = chain[0];
    const last = chain[chain.length - 1];
    const merged = {
      id: first.id,
      lineType: first.lineType || 'conduit',
      points: mergedPts,
      conduitSpec: first.conduitSpec,
      footage: polylineLength(mergedPts),
      street: first.street || '',
      notes: first.notes || '',
      startSnap: first.startSnap || null,
      endSnap: last.endSnap || null,
      parentId: first.parentId,
      fiberIndex: first.fiberIndex,
    };

    // Store undo data for all removed segments
    const removedIds = segs.map(s => s.id).filter(id => id !== first.id);
    setSegments(prev => {
      const filtered = prev.filter(s => !ids.has(s.id));
      return [...filtered, merged];
    });
    setUndoStack(prev => [...prev, { action: 'merge', mergedId: first.id, originalSegments: segs.map(s => ({ ...s, points: [...s.points] })) }]);
    setRedoStack([]);
    setMultiSelectedIds(new Set());
    setSelectedId(merged.id);
  }, [segments, selectedId, multiSelectedIds]);

  // ---- Undo (pushes to redo) ----
  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;
    const last = undoStack[undoStack.length - 1];
    if (last.action === 'addSegment') {
      const seg = segments.find(s => s.id === last.id);
      if (seg) setRedoStack(prev => [...prev, { action: 'addSegment', data: { ...seg } }]);
      setSegments(prev => prev.filter(s => s.id !== last.id));
    } else if (last.action === 'addDevice') {
      const dev = devices.find(d => d.id === last.id);
      if (dev) setRedoStack(prev => [...prev, { action: 'addDevice', data: { ...dev } }]);
      setDevices(prev => prev.filter(d => d.id !== last.id));
    } else if (last.action === 'merge') {
      // Undo merge: remove merged segment, restore originals
      const merged = segments.find(s => s.id === last.mergedId);
      if (merged) setRedoStack(prev => [...prev, { action: 'merge', mergedId: last.mergedId, mergedData: { ...merged, points: [...merged.points] }, originalSegments: last.originalSegments }]);
      setSegments(prev => [...prev.filter(s => s.id !== last.mergedId), ...last.originalSegments]);
    }
    setUndoStack(prev => prev.slice(0, -1));
    setSelectedId(null);
  }, [undoStack, segments, devices]);

  // ---- Redo ----
  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    const last = redoStack[redoStack.length - 1];
    if (last.action === 'addSegment') {
      setSegments(prev => [...prev, last.data]);
      setUndoStack(prev => [...prev, { action: 'addSegment', id: last.data.id }]);
    } else if (last.action === 'addDevice') {
      setDevices(prev => [...prev, last.data]);
      setUndoStack(prev => [...prev, { action: 'addDevice', id: last.data.id }]);
    }
    setRedoStack(prev => prev.slice(0, -1));
  }, [redoStack]);

  // ---- Fit all items ----
  const fitAllItems = useCallback(() => {
    if (!mapRef.current) return;
    const bounds = new window.google.maps.LatLngBounds();
    let hasPoints = false;
    segments.forEach(s => s.points.forEach(p => { bounds.extend(p); hasPoints = true; }));
    devices.forEach(d => { bounds.extend({ lat: d.lat, lng: d.lng }); hasPoints = true; });
    if (hasPoints) mapRef.current.fitBounds(bounds, 50);
  }, [segments, devices]);

  // ---- Export ----
  const exportData = useCallback(() => {
    const data = {
      project: 'SLPH.01.006',
      exported: new Date().toISOString(),
      segments: segments.map(s => ({
        id: s.id, lineType: s.lineType, points: s.points, conduitSpec: s.conduitSpec,
        footage: s.footage, street: s.street, notes: s.notes || '',
        gps_start_lat: s.points[0].lat, gps_start_lng: s.points[0].lng,
        gps_end_lat: s.points[s.points.length - 1].lat, gps_end_lng: s.points[s.points.length - 1].lng,
      })),
      devices: devices.map(d => ({
        id: d.id, type: d.type, lat: d.lat, lng: d.lng, subtype: d.subtype, notes: d.notes || '',
      })),
      iconScale,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `vexus_sulphur_006_traced_${Date.now()}.json`; a.click();
    URL.revokeObjectURL(url);
  }, [segments, devices, iconScale]);

  // ---- Import ----
  const importData = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (data.segments) setSegments(data.segments.map(s => ({ ...s, points: s.points || [{ lat: s.gps_start_lat, lng: s.gps_start_lng }, { lat: s.gps_end_lat, lng: s.gps_end_lng }] })));
        if (data.devices) setDevices(data.devices);
        if (data.iconScale) setIconScale(data.iconScale);
        const maxSeg = data.segments ? Math.max(0, ...data.segments.map(s => parseInt(s.id.split('-')[1]) || 0)) : 0;
        const maxDev = data.devices ? Math.max(0, ...data.devices.map(d => parseInt(d.id.split('-')[1]) || 0)) : 0;
        idCounter = Math.max(maxSeg, maxDev) + 1;
        setUndoStack([]); setRedoStack([]);
      } catch (err) { alert('Invalid JSON file'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  }, []);

  // ---- Computed ----
  const isSelected = useCallback((id) => id === selectedId || multiSelectedIds.has(id), [selectedId, multiSelectedIds]);
  const allSelectedIds = useMemo(() => {
    const s = new Set(multiSelectedIds);
    if (selectedId) s.add(selectedId);
    return s;
  }, [selectedId, multiSelectedIds]);
  const selectedSegment = useMemo(() => segments.find(s => s.id === selectedId), [segments, selectedId]);
  const selectedDevice = useMemo(() => devices.find(d => d.id === selectedId), [devices, selectedId]);
  const markerSize = useMemo(() => getMarkerSize(zoom) * iconScale, [zoom, iconScale]);

  // === IMPERATIVE DEVICE MARKERS ===
  // Managed directly via google.maps.Marker API — zero React overhead
  const iconCacheRef = useRef(new Map());
  const deviceMarkersRef = useRef(new Map()); // deviceId → google.maps.Marker

  // Stable handler refs — always point to current closures
  const deviceClickRef = useRef(null);
  const deviceMouseDownRef = useRef(null);

  deviceMouseDownRef.current = (deviceId, e) => {
    if (shiftHeldRef.current || shiftLockedRef.current) {
      if (e.stop) e.stop();
      skipMapClickRef.current = true;
      const dev = devicesRef.current.find(x => x.id === deviceId);
      if (!dev) return;
      const aids = new Set(multiSelectedIds);
      if (selectedId) aids.add(selectedId);
      const ids = aids.size > 1 && aids.has(deviceId) ? aids : new Set([deviceId]);
      shiftDragRef.current = { type: ids.size > 1 ? 'multi' : 'device', id: deviceId, ids, lastLat: dev.lat, lastLng: dev.lng };
    }
  };

  deviceClickRef.current = (deviceId, e) => {
    if (e && e.stop) e.stop();
    skipMapClickRef.current = true;
    if (shiftHeldRef.current || shiftLockedRef.current) return;
    const dev = devicesRef.current.find(x => x.id === deviceId);
    if (!dev) return;
    const isDrawTool = activeTool === TOOLS.CONDUIT || activeTool === TOOLS.FIBER;
    if (isDrawTool) {
      let snapPt;
      const refPt = drawingPointsRef.current.length > 0
        ? drawingPointsRef.current[drawingPointsRef.current.length - 1]
        : { lat: dev.lat, lng: dev.lng };
      if (dev.type === 'handhole') {
        const snapPts = getHandholeSnapPoints(dev, zoom, iconScale);
        const ci = nearestCornerIndex(refPt, snapPts);
        snapPt = { ...snapPts[ci], _snap: { deviceId: dev.id, cornerIndex: ci } };
      } else if (BOWTIE_TYPES.has(dev.type)) {
        const snapPts = getBowtieSnapPoints(dev, zoom, iconScale);
        const ci = nearestCornerIndex(refPt, snapPts);
        snapPt = { ...snapPts[ci], _snap: { deviceId: dev.id, cornerIndex: ci } };
      } else {
        snapPt = { lat: dev.lat, lng: dev.lng, _snap: { deviceId: dev.id, cornerIndex: -1 } };
      }
      setDrawingPoints(prev => [...prev, snapPt]);
      if (drawingPointsRef.current.length >= 1) pendingFinishRef.current = true;
      return;
    }
    let newDevType;
    if (activeTool === TOOLS.SPLICE_POINT || activeTool === TOOLS.SPLITTER_1x4 || activeTool === TOOLS.SPLITTER_2x8) {
      const spliceEntry = SPLICE_TYPES.find(t => t.value === spliceSubtype);
      newDevType = spliceEntry?.deviceType || 'splice_point';
    } else {
      const dtm = { [TOOLS.HANDHOLE]: 'handhole', [TOOLS.FLOWERPOT]: 'flowerpot', [TOOLS.SLACK_LOOP]: 'slack_loop' };
      newDevType = dtm[activeTool];
    }
    if (newDevType) {
      const prefixes = { handhole: 'HH', flowerpot: 'FP', splitter_1x4: 'TB', splitter_2x8: 'SP', splice_point: 'SC', slack_loop: 'SL' };
      const newDev = {
        id: nextId(prefixes[newDevType] || 'DV'), type: newDevType,
        lat: dev.lat, lng: dev.lng,
        subtype: newDevType === 'handhole' ? handholeType : newDevType === 'slack_loop' ? slackType : spliceSubtype,
        rotation: newDevType === 'slack_loop' ? getNearestLineAngle({ lat: dev.lat, lng: dev.lng }, segmentsRef.current) : undefined,
        notes: '',
      };
      setDevices(prev => [...prev, newDev]);
      setUndoStack(prev => [...prev, { action: 'addDevice', id: newDev.id }]);
      setRedoStack([]);
      if (!ctrlHeldRef.current && !toolLockedRef.current) { setSelectedId(newDev.id); setActiveTool(TOOLS.SELECT); }
      return;
    }
    const domEvt = e && e.domEvent;
    if (domEvt?.ctrlKey && domEvt?.shiftKey) { toggleMultiSelect(deviceId); }
    else { setMultiSelectedIds(new Set()); setSelectedId(deviceId); setActiveTool(TOOLS.SELECT); }
  };

  // Build icon from cache
  const getDeviceIcon = useCallback((d, sel) => {
    const sz = d.type === 'flowerpot' ? markerSize * 0.7 : d.type === 'splitter_1x4' ? markerSize * 0.8 : d.type === 'splice_point' ? markerSize * 0.6 : markerSize;
    const cacheKey = `${d.type}-${sz}-${sel}-${d.subtype||''}-${d.rotation||0}`;
    if (iconCacheRef.current.has(cacheKey)) return iconCacheRef.current.get(cacheKey);
    const { svg, w, h } = generateDeviceSvg(d.type, sz, sel, d.subtype, d.rotation);
    const icon = {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new window.google.maps.Size(w, h),
      anchor: new window.google.maps.Point(w / 2, h / 2),
    };
    iconCacheRef.current.set(cacheKey, icon);
    return icon;
  }, [markerSize]);

  // Filtered segments/devices by layer visibility (must be above imperative marker useEffect)
  const visibleSegments = useMemo(() => segments.filter(s => s.lineType === 'fiber' ? showFiber : showConduit), [segments, showConduit, showFiber]);
  const visibleDevices = useMemo(() => showDevices ? devices : [], [devices, showDevices]);

  // Imperative marker sync — only update what changed
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clear icon cache on zoom change (sizes differ)
    iconCacheRef.current.clear();

    const currentIds = new Set(visibleDevices.map(d => d.id));

    // Remove markers for devices no longer visible
    for (const [id, marker] of deviceMarkersRef.current) {
      if (!currentIds.has(id)) {
        marker.setMap(null);
        deviceMarkersRef.current.delete(id);
      }
    }

    // Create or update markers
    for (const d of visibleDevices) {
      const sel = isSelected(d.id);
      const icon = getDeviceIcon(d, sel);
      const existing = deviceMarkersRef.current.get(d.id);

      if (existing) {
        // Update position if changed
        const pos = existing.getPosition();
        if (pos.lat() !== d.lat || pos.lng() !== d.lng) {
          existing.setPosition({ lat: d.lat, lng: d.lng });
        }
        existing.setIcon(icon);
        existing.setZIndex(sel ? 150 : 50);
      } else {
        // Create new marker
        const marker = new window.google.maps.Marker({
          position: { lat: d.lat, lng: d.lng },
          map,
          icon,
          zIndex: sel ? 150 : 50,
          clickable: true,
        });
        const devId = d.id;
        marker.addListener('click', (e) => deviceClickRef.current(devId, e));
        marker.addListener('mousedown', (e) => deviceMouseDownRef.current(devId, e));
        deviceMarkersRef.current.set(devId, marker);
      }
    }

    // Cleanup on unmount
    return () => {};
  }, [visibleDevices, selectedId, multiSelectedIds, markerSize, isSelected, getDeviceIcon]);
  const conduitWeight = useMemo(() => getConduitWeight(zoom), [zoom]);
  const fiberWeight = useMemo(() => getFiberWeight(zoom), [zoom]);
  const totalFootage = useMemo(() => segments.reduce((sum, s) => sum + (s.footage || 0), 0), [segments]);
  const activeSpec = activeTool === TOOLS.FIBER ? fiberSpec : conduitSpec;
  const activeWeight = activeTool === TOOLS.FIBER ? fiberWeight : conduitWeight;
  const measureDist = useMemo(() => measurePoints.length === 2 ? distanceFt(measurePoints[0], measurePoints[1]) : null, [measurePoints]);

  // Viewport-culled devices — only render for devices within map bounds (+ small padding)
  const viewportDevices = useMemo(() => {
    if (!mapBounds) return visibleDevices;
    const pad = (mapBounds.north - mapBounds.south) * 0.1; // 10% padding
    return visibleDevices.filter(d =>
      d.lat >= mapBounds.south - pad && d.lat <= mapBounds.north + pad &&
      d.lng >= mapBounds.west - pad && d.lng <= mapBounds.east + pad
    );
  }, [visibleDevices, mapBounds]);

  // Device lookup map for O(1) access instead of O(n) find() per segment
  const deviceMap = useMemo(() => {
    const map = new Map();
    devices.forEach(d => map.set(d.id, d));
    return map;
  }, [devices]);

  const previewPath = useMemo(() => {
    if (drawingPoints.length === 0) return null;
    const pts = [...drawingPoints];
    if (mousePos) pts.push(mousePos);
    return pts;
  }, [drawingPoints, mousePos]);

  if (!isLoaded) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d1b2a', color: '#fff' }}>Loading Google Maps...</div>;
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#0d1b2a', color: '#fff', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>

      {/* ---- TOP BAR ---- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#112240', borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0, zIndex: 10 }}>
        <button onClick={() => setCurrentPage && setCurrentPage('project-map')} style={btnStyle('#334155')}>Back</button>
        <div style={{ fontSize: 16, fontWeight: 700, marginRight: 'auto' }}>
          Map Tracer <span style={{ fontSize: 12, color: '#888', fontWeight: 400 }}>Vexus Sulphur 006</span>
        </div>
        {autoSaveStatus && <span style={{ fontSize: 10, color: '#4ade80' }}>{autoSaveStatus}</span>}
        {toolLocked && <span style={{ fontSize: 10, padding: '2px 6px', background: '#7c3aed', borderRadius: 4, color: '#fff', fontWeight: 600 }}>TOOL LOCKED</span>}
        {shiftActive && <span style={{ fontSize: 10, padding: '2px 6px', background: '#0891b2', borderRadius: 4, color: '#fff', fontWeight: 600 }}>SHIFT DRAG</span>}
        {allSelectedIds.size > 1 && <>
          <span style={{ fontSize: 10, padding: '2px 6px', background: '#b45309', borderRadius: 4, color: '#fff', fontWeight: 600 }}>{allSelectedIds.size} SELECTED</span>
          <button onClick={handleMerge} style={{ ...btnStyle('#065f46'), fontSize: 11, padding: '3px 10px' }} title="Join selected segments into one line (J)">Merge (J)</button>
        </>}
        <div style={{ fontSize: 12, color: '#8892b0' }}>
          {segments.length} segs ({totalFootage} LF) | {devices.length} devices
        </div>
      </div>

      {/* === GROUPED TOOLBAR === */}
      <div style={{
        position: 'absolute', top: 10, left: 10, right: 10,
        display: 'flex', gap: 4, alignItems: 'flex-start', zIndex: 100,
        flexWrap: 'wrap',
      }}>
        {/* PROJECT group */}
        <div style={toolbarGroupStyle(mapDarkMode)}>
          <div style={tgLabel(mapDarkMode)}>PROJECT</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <select value={projectId} onChange={e => setProjectId(e.target.value)}
              style={{ ...tgSelect(mapDarkMode), width: 140 }}>
              {projectsList.length > 0 ? projectsList.map(p => (
                <option key={p.id} value={p.id}>{p.id}</option>
              )) : <option value="SLPH.01.006">Vexus Sulphur 006</option>}
            </select>
            <button onClick={() => syncFromSheets({ confirm: true })} disabled={isSyncing}
              style={btnStyle(isSyncing ? '#666' : '#2196F3')}
              title="Sync from Google Sheets">
              {isSyncing ? '⏳' : '⬇'} {isSyncing ? '...' : 'Sync'}
            </button>
            <button onClick={handlePublish} disabled={isPublishing}
              style={btnStyle(isPublishing ? '#666' : '#4CAF50')}
              title="Publish to live map (Ctrl+P)">
              {isPublishing ? '⏳' : '☁️'} {isPublishing ? '...' : 'Pub'}
            </button>
            <label style={{ fontSize: 9, color: mapDarkMode ? '#aaa' : '#666', display: 'flex', alignItems: 'center', gap: 2 }}>
              <input type="checkbox" checked={autoPushEnabled} onChange={e => setAutoPushEnabled(e.target.checked)}
                title="Auto-push every 5 min" style={{ width: 12, height: 12 }} />Auto
            </label>
            <button onClick={startAutoBackup}
              style={btnStyle(autoBackupEnabled ? '#4CAF50' : '#1e3a5f')}
              title={autoBackupEnabled ? 'Auto-backup active (every 3 min)' : 'Save backup to PC (auto-saves every 3 min)'}>
              💾 {autoBackupEnabled ? 'Backing Up' : 'Backup'}
            </button>
          </div>
          {(lastPublished || lastSynced) && (
            <div style={{ fontSize: 9, color: lastPublished ? '#4CAF50' : '#2196F3', marginTop: 2 }}>
              {lastPublished && <>Pub {lastPublished.toLocaleTimeString()}
                {publishResult?.counts && ` (${publishResult.counts.segments}s ${
                  (publishResult.counts.handholes||0) + (publishResult.counts.flowerpots||0) + (publishResult.counts.splicePoints||0) + (publishResult.counts.groundRods||0)
                }d)`}
              </>}
              {lastPublished && lastSynced && ' | '}
              {lastSynced && <span style={{ color: '#2196F3' }}>Synced {lastSynced.toLocaleTimeString()}</span>}
            </div>
          )}
        </div>

        {/* DRAW group */}
        <div style={toolbarGroupStyle(mapDarkMode)}>
          <div style={tgLabel(mapDarkMode)}>DRAW</div>
          <div style={{ display: 'flex', gap: 2 }}>
            <button onClick={() => setActiveTool(TOOLS.SELECT)} style={btnStyle(activeTool === TOOLS.SELECT ? '#c850c0' : undefined)} title="Select (Esc)">Sel</button>
            <button onClick={() => setActiveTool(TOOLS.CONDUIT)} style={btnStyle(activeTool === TOOLS.CONDUIT ? '#FF6B00' : undefined)} title="Conduit (1)">Cond</button>
            <button onClick={() => setActiveTool(TOOLS.FIBER)} style={btnStyle(activeTool === TOOLS.FIBER ? '#FFD700' : undefined)} title="Fiber (2)">Fib</button>
            <button onClick={() => setActiveTool(TOOLS.MEASURE)} style={btnStyle(activeTool === TOOLS.MEASURE ? '#00BCD4' : undefined)} title="Measure (M)">Msr</button>
          </div>
          {(activeTool === TOOLS.CONDUIT || activeTool === TOOLS.FIBER) && (
            <select value={activeTool === TOOLS.CONDUIT ? conduitSpec : fiberSpec}
              onChange={e => activeTool === TOOLS.CONDUIT ? setConduitSpec(e.target.value) : setFiberSpec(e.target.value)}
              style={tgSelect(mapDarkMode)}>
              {(activeTool === TOOLS.CONDUIT ? CONDUIT_SPECS : FIBER_SPECS).map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          )}
        </div>

        {/* PLACE group */}
        <div style={toolbarGroupStyle(mapDarkMode)}>
          <div style={tgLabel(mapDarkMode)}>PLACE</div>
          <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <button onClick={() => setActiveTool(TOOLS.HANDHOLE)} style={btnStyle(activeTool === TOOLS.HANDHOLE ? '#2196F3' : undefined)} title="Handhole (4)">HH</button>
            <button onClick={() => setActiveTool(TOOLS.FLOWERPOT)} style={btnStyle(activeTool === TOOLS.FLOWERPOT ? '#FF9800' : undefined)} title="Flowerpot (5)">FP</button>
            <button onClick={() => setActiveTool(TOOLS.SPLICE_POINT)} style={btnStyle(activeTool === TOOLS.SPLICE_POINT ? '#9C27B0' : undefined)} title="Splice Point (6)">SP</button>
            <button onClick={() => setActiveTool(TOOLS.SLACK_LOOP)} style={btnStyle(activeTool === TOOLS.SLACK_LOOP ? '#00BCD4' : undefined)} title="Slack Loop (7)">SL</button>
            <button onClick={() => setActiveTool(TOOLS.GROUND_ROD)} style={btnStyle(activeTool === TOOLS.GROUND_ROD ? '#795548' : undefined)} title="Ground Rod (8)">GR</button>
          </div>
          {activeTool === TOOLS.HANDHOLE && handholeType !== undefined && (
            <select value={handholeType} onChange={e => setHandholeType(e.target.value)}
              style={tgSelect(mapDarkMode)}>
              {HANDHOLE_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          )}
          {activeTool === TOOLS.SPLICE_POINT && (
            <select value={spliceSubtype} onChange={e => setSpliceSubtype(e.target.value)}
              style={tgSelect(mapDarkMode)}>
              {SPLICE_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          )}
        </div>

        {/* VIEW group */}
        <div style={toolbarGroupStyle(mapDarkMode)}>
          <div style={tgLabel(mapDarkMode)}>VIEW</div>
          <div style={{ display: 'flex', gap: 2 }}>
            <button onClick={() => setSnapEnabled(p => !p)} style={btnStyle(snapEnabled ? '#4CAF50' : undefined)} title="Snap (S)">Snap</button>
            <button onClick={() => setShowLabels(p => !p)} style={btnStyle(showLabels ? '#4CAF50' : undefined)} title="Labels">Lbl</button>
            <button onClick={() => setShowLegend(p => !p)} style={btnStyle(showLegend ? '#4CAF50' : undefined)} title="Legend (L)">Leg</button>
          </div>
        </div>

        {/* ACTIONS group */}
        <div style={toolbarGroupStyle(mapDarkMode)}>
          <div style={tgLabel(mapDarkMode)}>ACTIONS</div>
          <div style={{ display: 'flex', gap: 2 }}>
            <button onClick={handleUndo} style={btnStyle(undefined)} title="Undo (Ctrl+Z)">↩</button>
            <button onClick={handleRedo} style={btnStyle(undefined)} title="Redo (Ctrl+Y)">↪</button>
            <button onClick={() => setShowStyleSettings(p => !p)} style={btnStyle(showStyleSettings ? '#c850c0' : undefined)} title="Style settings">🎨</button>
            <button onClick={() => setShowBillingSummary(p => !p)} style={btnStyle(showBillingSummary ? '#4CAF50' : undefined)} title="Billing (B)">💰</button>
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowMoreMenu(p => !p)} style={btnStyle(showMoreMenu ? '#c850c0' : undefined)} title="More">⋯</button>
              {showMoreMenu && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 4,
                  background: mapDarkMode ? '#1a1a2e' : '#fff', border: `1px solid ${mapDarkMode ? '#333' : '#ddd'}`,
                  borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.3)', minWidth: 180, zIndex: 200,
                }}>
                  {[
                    { label: '📥 Import JSON', action: () => { const inp = document.createElement('input'); inp.type = 'file'; inp.accept = '.json'; inp.onchange = handleImportJson; inp.click(); } },
                    { label: '📤 Export JSON', action: () => handleExportJson?.() },
                    { label: '📋 Load from Sheets', action: () => handleLoadFromSheets?.() },
                    { label: '📂 Import Extraction', action: () => handleImportExtractionJson?.() },
                    { label: '📸 Snapshots', action: () => setShowVersionSnapshots(p => !p) },
                    { label: '⌨️ Shortcuts', action: () => setShowShortcutsModal(true) },
                    { label: '🖨️ Print', action: () => window.print() },
                  ].map((item, i) => (
                    <button key={i} onClick={() => { item.action(); setShowMoreMenu(false); }}
                      style={{
                        display: 'block', width: '100%', padding: '8px 14px', border: 'none',
                        background: 'transparent', color: mapDarkMode ? '#e0e0e0' : '#333',
                        textAlign: 'left', cursor: 'pointer', fontSize: 12,
                        borderBottom: i < 6 ? `1px solid ${mapDarkMode ? '#222' : '#f0f0f0'}` : 'none',
                      }}
                      onMouseEnter={e => e.target.style.background = mapDarkMode ? '#16213e' : '#f5f5f5'}
                      onMouseLeave={e => e.target.style.background = 'transparent'}
                    >{item.label}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ---- ACTIVE TOOL INDICATOR ---- */}
      {activeTool !== TOOLS.SELECT && (
        <div style={{ padding: '4px 12px', fontSize: 12, background: TOOL_CONFIG[activeTool].color + '20', color: TOOL_CONFIG[activeTool].color, fontWeight: 600, textAlign: 'center', borderBottom: `2px solid ${TOOL_CONFIG[activeTool].color}`, flexShrink: 0 }}>
          {activeTool === TOOLS.MEASURE
            ? (measurePoints.length === 0 ? 'Click two points to measure distance. Right-click or Escape to cancel.'
              : measurePoints.length === 1 ? 'Click second point to complete measurement.'
              : `Distance: ${measureDist} ft (${(measureDist / 5280).toFixed(3)} mi) -- Click to start new measurement`)
            : (activeTool === TOOLS.CONDUIT || activeTool === TOOLS.FIBER)
              ? (drawingPoints.length === 0
                  ? `Click to start drawing. Double-click to finish.${snapEnabled ? ' Snap ON (30 ft).' : ''}`
                  : `Drawing: ${drawingPoints.length} vertices (${polylineLength(drawingPoints)} LF) -- Dbl-click finish, Right-click undo vertex, Esc cancel`)
              : `Click on the map to place a ${TOOL_CONFIG[activeTool].label}`
          }
        </div>
      )}

      {/* ---- MAP + PANELS ---- */}
      <div style={{ flex: 1, display: 'flex', position: 'relative', overflow: 'hidden' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <GoogleMap
            mapContainerStyle={MAP_CONTAINER}
            center={mapCenter}
            zoom={zoom}
            onLoad={onMapLoad}
            onZoomChanged={onZoomChanged}
            onClick={onMapClick}
            onDblClick={onMapDblClick}
            onRightClick={onMapRightClick}
            onMouseMove={onMouseMove}
            options={{
              mapTypeId,
              disableDoubleClickZoom: true,
              gestureHandling: 'greedy',
              streetViewControl: false, fullscreenControl: false, mapTypeControl: false,
              zoomControl: false, scaleControl: false, rotateControl: false, panControl: false,
              keyboardShortcuts: false,
              // styles removed — incompatible with pinned Maps API v3.58
              draggableCursor: shiftActive ? 'move' : (TOOL_CONFIG[activeTool]?.cursor || 'default'),
              draggingCursor: shiftActive ? 'grabbing' : 'grabbing',
            }}
          >
            {/* Segments: conduit outlines (dark border behind colored line) */}
            {visibleSegments.filter(s => s.lineType !== 'fiber').map(seg => {
              const path = resolveSnappedPath(seg, deviceMap, zoom, iconScale);
              const sel = isSelected(seg.id);
              const color = sel ? '#FFD700' : getLineColor(seg.conduitSpec, styleOverrides);
              const w = sel ? conduitWeight + 2 : conduitWeight;
              return (
                <React.Fragment key={`${seg.id}-${seg.conduitSpec}-${color}-${sel}`}>
                  <Polyline path={path}
                    options={{
                      strokeColor: sel ? '#B8860B' : '#000000',
                      strokeWeight: w + 3, strokeOpacity: sel ? 0.9 : 0.6,
                      clickable: false, zIndex: sel ? 99 : 9,
                    }} />
                  <Polyline path={path}
                    options={{
                      strokeColor: color, strokeWeight: w, strokeOpacity: 1.0,
                      clickable: true, zIndex: sel ? 100 : 10,
                    }}
                    onMouseDown={(e) => {
                      if (shiftHeldRef.current || shiftLockedRef.current) {
                        if (e.stop) e.stop(); skipMapClickRef.current = true;
                        // Shift-drag all selected if multi-selected
                        const ids = allSelectedIds.size > 1 ? allSelectedIds : new Set([seg.id]);
                        shiftDragRef.current = { type: 'multi', ids, lastLat: e.latLng.lat(), lastLng: e.latLng.lng() };
                      }
                    }}
                    onClick={(e) => {
                      if (e.stop) e.stop(); skipMapClickRef.current = true;
                      if (e.domEvent?.ctrlKey && e.domEvent?.shiftKey) { toggleMultiSelect(seg.id); }
                      else { setMultiSelectedIds(new Set()); setSelectedId(seg.id); setActiveTool(TOOLS.SELECT); }
                    }} />
                </React.Fragment>
              );
            })}
            {/* Fiber cables on top with outline */}
            {visibleSegments.filter(s => s.lineType === 'fiber').map(seg => {
              const sel = isSelected(seg.id);
              const resolvedPts = resolveSnappedPath(seg, deviceMap, zoom, iconScale);
              const path = (seg.parentId && typeof seg.fiberIndex === 'number')
                ? offsetPath(resolvedPts, getFiberOffset(zoom, seg.fiberIndex))
                : resolvedPts;
              const color = sel ? '#FFD700' : getLineColor(seg.conduitSpec, styleOverrides);
              const w = sel ? fiberWeight + 2 : fiberWeight;
              return (
                <React.Fragment key={`${seg.id}-${seg.conduitSpec}-${color}-${sel}`}>
                  <Polyline path={path}
                    options={{
                      strokeColor: sel ? '#B8860B' : '#000000',
                      strokeWeight: w + 2, strokeOpacity: sel ? 0.9 : 0.5,
                      clickable: false, zIndex: sel ? 99 : 19,
                    }} />
                  <Polyline path={path}
                    options={{
                      strokeColor: color, strokeWeight: w, strokeOpacity: 1.0,
                      clickable: true, zIndex: sel ? 100 : 20,
                    }}
                    onMouseDown={(e) => {
                      if (shiftHeldRef.current || shiftLockedRef.current) {
                        if (e.stop) e.stop(); skipMapClickRef.current = true;
                        const ids = allSelectedIds.size > 1 ? allSelectedIds : new Set([seg.id]);
                        shiftDragRef.current = { type: 'multi', ids, lastLat: e.latLng.lat(), lastLng: e.latLng.lng() };
                      }
                    }}
                    onClick={(e) => {
                      if (e.stop) e.stop(); skipMapClickRef.current = true;
                      if (e.domEvent?.ctrlKey && e.domEvent?.shiftKey) { toggleMultiSelect(seg.id); }
                      else { setMultiSelectedIds(new Set()); setSelectedId(seg.id); setActiveTool(TOOLS.SELECT); }
                    }} />
                </React.Fragment>
              );
            })}

            {/* Drawing preview */}
            {previewPath && previewPath.length >= 2 && (
              <Polyline path={previewPath}
                options={{ strokeColor: getLineColor(activeSpec, styleOverrides), strokeWeight: activeWeight, strokeOpacity: 0.6, clickable: false, zIndex: 200 }} />
            )}
            {drawingPoints.map((pt, i) => (
              <OverlayView key={`dv-${i}`} position={pt} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                <div style={{ position: 'absolute', transform: 'translate(-50%, -50%)', width: 10, height: 10, borderRadius: '50%', background: getLineColor(activeSpec, styleOverrides), border: '2px solid #fff', boxShadow: '0 0 4px rgba(0,0,0,0.5)' }} />
              </OverlayView>
            ))}

            {/* Device markers rendered imperatively via useEffect — no JSX needed */}

            {/* Segment midpoint labels removed — were causing lag */}

            {/* Draggable vertex handles for selected segment — use resolved snap positions */}
            {selectedSegment && resolveSnappedPath(selectedSegment, deviceMap, zoom, iconScale).map((pt, i) => (
              <Marker key={`vtx-${selectedSegment.id}-${i}`} position={pt} draggable={true}
                icon={{ path: window.google?.maps?.SymbolPath?.CIRCLE || 0, scale: 7, fillColor: '#FFD700', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 }}
                zIndex={200}
                onDrag={(e) => {
                  const segId = selectedSegment.id;
                  const idx = i;
                  setSegments(prev => prev.map(s => {
                    if (s.id !== segId) return s;
                    const newPoints = [...s.points];
                    newPoints[idx] = { lat: e.latLng.lat(), lng: e.latLng.lng() };
                    return { ...s, points: newPoints, footage: polylineLength(newPoints) };
                  }));
                }} />
            ))}

            {/* Draggable marker for selected device */}
            {selectedDevice && (
              <Marker position={{ lat: selectedDevice.lat, lng: selectedDevice.lng }} draggable={true}
                icon={{ path: window.google?.maps?.SymbolPath?.CIRCLE || 0, scale: 9, fillColor: '#FFD700', fillOpacity: 0.5, strokeColor: '#FFD700', strokeWeight: 2 }}
                zIndex={199}
                onDrag={(e) => {
                  const devId = selectedDevice.id;
                  setDevices(prev => prev.map(d => d.id === devId ? { ...d, lat: e.latLng.lat(), lng: e.latLng.lng() } : d));
                }} />
            )}

            {/* Measurement line */}
            {measurePoints.length === 2 && (
              <>
                <Polyline path={measurePoints} options={{ strokeColor: '#FFD700', strokeWeight: 3, strokeOpacity: 0.9, clickable: false, zIndex: 300, icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 }, offset: '0', repeat: '15px' }] }} />
                <OverlayView position={{ lat: (measurePoints[0].lat + measurePoints[1].lat) / 2, lng: (measurePoints[0].lng + measurePoints[1].lng) / 2 }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                  <div style={{ position: 'absolute', transform: 'translate(-50%, -50%)', background: '#FFD700', color: '#000', padding: '2px 8px', borderRadius: 4, fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', pointerEvents: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                    {measureDist} ft
                  </div>
                </OverlayView>
              </>
            )}
            {measurePoints.map((pt, i) => (
              <Marker key={`mp-${i}`} position={pt}
                icon={{ path: window.google?.maps?.SymbolPath?.CIRCLE || 0, scale: 6, fillColor: '#FFD700', fillOpacity: 1, strokeColor: '#000', strokeWeight: 2 }}
                zIndex={301} />
            ))}

            {/* Footage entry popup after dropping a line */}
            {footagePopup && footagePopup.position && (() => {
              const popupSeg = segments.find(s => s.id === footagePopup.segId);
              if (!popupSeg) return null;
              return (
                <OverlayView position={footagePopup.position} mapPaneName={OverlayView.FLOAT_PANE}>
                  <div style={{ position: 'absolute', transform: 'translate(-50%, -120%)', background: '#1e293b', border: '2px solid #FFD700', borderRadius: 8, padding: '8px 10px', boxShadow: '0 4px 20px rgba(0,0,0,0.6)', zIndex: 500, minWidth: 140 }}
                    onClick={e => e.stopPropagation()}>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>{popupSeg.id} — Footage (LF)</div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <input type="number" autoFocus defaultValue={popupSeg.footage || 0}
                        style={{ background: '#0f172a', color: '#fff', border: '1px solid #475569', borderRadius: 4, padding: '4px 8px', fontSize: 14, fontWeight: 700, width: 80, outline: 'none' }}
                        onFocus={e => e.target.select()}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            const val = parseInt(e.target.value) || 0;
                            setSegments(prev => prev.map(s => s.id === footagePopup.segId ? { ...s, footage: val } : s));
                            setFootagePopup(null);
                          }
                          if (e.key === 'Escape') setFootagePopup(null);
                        }} />
                      <button onClick={(e) => {
                        const input = e.target.parentElement.querySelector('input');
                        const val = parseInt(input?.value) || 0;
                        setSegments(prev => prev.map(s => s.id === footagePopup.segId ? { ...s, footage: val } : s));
                        setFootagePopup(null);
                      }} style={{ background: '#166534', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>OK</button>
                      <button onClick={() => setFootagePopup(null)}
                        style={{ background: '#7f1d1d', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 6px', fontSize: 11, cursor: 'pointer' }}>X</button>
                    </div>
                  </div>
                </OverlayView>
              );
            })()}

          </GoogleMap>

          {/* Zoom buttons (absolute on map) */}
          <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 15, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button onClick={() => mapRef.current?.setZoom((mapRef.current.getZoom() || 16) + 1)} style={{ ...zoomBtnStyle }}>+</button>
            <button onClick={() => mapRef.current?.setZoom((mapRef.current.getZoom() || 16) - 1)} style={{ ...zoomBtnStyle }}>-</button>
            <button onClick={fitAllItems} style={{ ...zoomBtnStyle, fontSize: 10 }} title="Fit all items">Fit</button>
          </div>
        </div>

        {/* ---- LEGEND PANEL ---- */}
        {showLegend && (
          <div style={{ position: 'absolute', bottom: 8, right: 8, zIndex: 20, background: 'rgba(17,34,64,0.95)', borderRadius: 8, padding: 12, width: 200, maxHeight: 400, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Color Legend</div>
            <div style={{ fontSize: 10, color: '#8892b0', marginBottom: 4 }}>CONDUIT (thick)</div>
            {CONDUIT_SPECS.map(c => (
              <div key={c.value} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', fontSize: 10 }}>
                <div style={{ width: 20, height: 4, background: c.color, borderRadius: 1, flexShrink: 0 }} />
                <span style={{ color: '#ccc' }}>{c.label}</span>
              </div>
            ))}
            <div style={{ fontSize: 10, color: '#8892b0', marginTop: 8, marginBottom: 4 }}>FIBER CABLES</div>
            {FIBER_SPECS.map(c => (
              <div key={c.value} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', fontSize: 10 }}>
                <div style={{ width: 20, height: 3, background: c.color, borderRadius: 1, flexShrink: 0 }} />
                <span style={{ color: '#ccc' }}>{c.label}</span>
              </div>
            ))}
            <div style={{ fontSize: 10, color: '#8892b0', marginTop: 8, marginBottom: 4 }}>DEVICES</div>
            {[
              { icon: '\u25A1', color: '#00FF00', label: 'Handhole' },
              { icon: '\u25CF', color: '#FF0000', label: 'Flowerpot' },
              { icon: '\u22C8', color: '#139A47', label: '1x4 Splitter' },
              { icon: '\u22C8', color: '#FF4444', label: '1x8 Splitter' },
              { icon: '\u22C8', color: '#FF0000', label: 'Splice Point' },
            ].map((d, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', fontSize: 10 }}>
                <span style={{ color: d.color, fontSize: 14, width: 20, textAlign: 'center' }}>{d.icon}</span>
                <span style={{ color: '#ccc' }}>{d.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* ---- SELECTION PANEL ---- */}
        {showPanel && (selectedSegment || selectedDevice) && (
          <div style={{ width: 320, background: '#112240', borderLeft: '1px solid rgba(255,255,255,0.1)', overflowY: 'auto', padding: 16, flexShrink: 0 }}>
            {selectedSegment && (
              <SegmentPanel segment={selectedSegment}
                segments={segments}
                onUpdate={(field, value) => setSegments(prev => prev.map(s => s.id === selectedSegment.id ? { ...s, [field]: value } : s))}
                onDelete={() => handleDelete(selectedSegment.id)}
                onPullFiber={(fiberList, side) => {
                  // Count existing fibers on each side to stack new ones after them
                  const existing = segments.filter(s => s.parentId === selectedSegment.id);
                  const topCount = existing.filter(s => (s.fiberIndex || 0) >= 0).length;
                  const bottomCount = existing.filter(s => (s.fiberIndex || 0) < 0).length;
                  const startIdx = side === 'bottom' ? -(bottomCount + 1) : topCount;

                  const newFibers = fiberList.map((spec, i) => ({
                    id: nextId('FBR'),
                    lineType: 'fiber',
                    points: [...selectedSegment.points],
                    conduitSpec: spec,
                    footage: selectedSegment.footage || polylineLength(selectedSegment.points),
                    street: selectedSegment.street || '',
                    notes: '',
                    parentId: selectedSegment.id,
                    fiberIndex: side === 'bottom' ? startIdx - i : startIdx + i,
                  }));
                  setSegments(prev => [...prev, ...newFibers]);
                  setUndoStack(prev => [...prev, ...newFibers.map(f => ({ action: 'addSegment', id: f.id }))]);
                  setRedoStack([]);
                }} />
            )}
            {selectedDevice && (
              <DevicePanel device={selectedDevice}
                onUpdate={(field, value) => setDevices(prev => prev.map(d => d.id === selectedDevice.id ? { ...d, [field]: value } : d))}
                onDelete={() => handleDelete(selectedDevice.id)} />
            )}
          </div>
        )}

        {/* === STATUS BAR === */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 28,
          background: darkMode ? 'rgba(13, 27, 42, 0.95)' : 'rgba(245, 245, 245, 0.95)',
          borderTop: `1px solid ${darkMode ? '#333' : '#ddd'}`,
          display: 'flex', alignItems: 'center', padding: '0 14px', gap: 20,
          fontSize: 11, color: darkMode ? '#888' : '#666', zIndex: 100,
        }}>
          <span>Conduit: {segments.filter(s => s.lineType !== 'fiber').reduce((sum, s) => sum + (s.footage || s.length || 0), 0).toLocaleString()} LF</span>
          <span>Fiber: {segments.filter(s => s.lineType === 'fiber').reduce((sum, s) => sum + (s.footage || s.length || 0), 0).toLocaleString()} LF</span>
          <span>Seg: {segments.length}</span>
          <span>Dev: {devices.length}</span>
          <span style={{ marginLeft: 'auto', fontFamily: 'monospace' }}>
            {activeTool ? activeTool.replace(/_/g, ' ') : 'select'}
          </span>
          {autoPushEnabled && <span style={{ color: '#4CAF50' }}>⟳ Auto</span>}
          {autoBackupEnabled && <span style={{ color: '#2196F3' }}>💾 {lastBackup ? lastBackup.toLocaleTimeString() : ''}</span>}
        </div>

        {/* === ITEMS LIST (Tabbed) === */}
        {itemsExpanded && (
          <div style={{
            position: 'absolute', bottom: 36, left: 10, width: 260,
            maxHeight: 300, background: darkMode ? 'rgba(26,26,46,0.95)' : 'rgba(255,255,255,0.95)',
            border: `1px solid ${darkMode ? '#333' : '#ddd'}`, borderRadius: 8,
            zIndex: 100, display: 'flex', flexDirection: 'column', overflow: 'hidden',
            backdropFilter: 'blur(8px)',
          }}>
            <div style={{ display: 'flex', borderBottom: `1px solid ${darkMode ? '#333' : '#ddd'}` }}>
              {['segments', 'devices'].map(tab => (
                <button key={tab} onClick={() => setItemsTab(tab)} style={{
                  flex: 1, padding: '6px 0', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                  textTransform: 'capitalize',
                  background: itemsTab === tab ? (darkMode ? '#0f3460' : '#e3f2fd') : 'transparent',
                  color: itemsTab === tab ? '#2196F3' : (darkMode ? '#aaa' : '#666'),
                  borderBottom: itemsTab === tab ? '2px solid #2196F3' : '2px solid transparent',
                }}>{tab} ({tab === 'segments' ? segments.length : devices.length})</button>
              ))}
              <button onClick={() => setItemsExpanded(false)} style={{
                padding: '4px 8px', border: 'none', background: 'transparent',
                color: darkMode ? '#888' : '#999', cursor: 'pointer', fontSize: 14,
              }}>−</button>
            </div>
            <input value={itemsSearch} onChange={e => setItemsSearch(e.target.value)}
              placeholder={`Filter ${itemsTab}...`}
              style={{
                margin: '6px 8px', padding: '4px 8px', borderRadius: 4,
                border: `1px solid ${darkMode ? '#444' : '#ccc'}`,
                background: darkMode ? '#16213e' : '#f8f9fa',
                color: darkMode ? '#e0e0e0' : '#333', fontSize: 11, outline: 'none',
              }} />
            <div style={{ overflowY: 'auto', flex: 1, maxHeight: 220 }}>
              {itemsTab === 'segments' && segments
                .filter(s => !itemsSearch || (s.street || s.conduitSpec || s.fiberSpec || s.id || '').toLowerCase().includes(itemsSearch.toLowerCase()))
                .map((seg, i) => (
                  <div key={seg.id || i} onClick={() => { setSelectedId(seg.id); }}
                    style={{
                      padding: '5px 10px', cursor: 'pointer', fontSize: 11,
                      borderBottom: `1px solid ${darkMode ? '#222' : '#f0f0f0'}`,
                      background: selectedSegment?.id === seg.id ? (darkMode ? '#0f3460' : '#e3f2fd') : 'transparent',
                      color: darkMode ? '#e0e0e0' : '#333',
                    }}>
                    <span style={{ fontWeight: 600 }}>{seg.conduitSpec || seg.fiberSpec || 'segment'}</span>
                    {seg.street && <span style={{ opacity: 0.6 }}> · {seg.street}</span>}
                    <span style={{ float: 'right', opacity: 0.5 }}>{(seg.footage || seg.length || 0).toFixed(0)}ft</span>
                  </div>
                ))
              }
              {itemsTab === 'devices' && devices
                .filter(d => !itemsSearch || (d.type || d.id || '').toLowerCase().includes(itemsSearch.toLowerCase()))
                .map((dev, i) => (
                  <div key={dev.id || i} onClick={() => { setSelectedId(dev.id); }}
                    style={{
                      padding: '5px 10px', cursor: 'pointer', fontSize: 11,
                      borderBottom: `1px solid ${darkMode ? '#222' : '#f0f0f0'}`,
                      background: selectedDevice?.id === dev.id ? (darkMode ? '#0f3460' : '#e3f2fd') : 'transparent',
                      color: darkMode ? '#e0e0e0' : '#333',
                    }}>
                    <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{(dev.type || '').replace(/_/g, ' ')}</span>
                    <span style={{ float: 'right', opacity: 0.5, fontSize: 10 }}>{dev.id}</span>
                  </div>
                ))
              }
            </div>
            {itemsTab === 'segments' && (
              <div style={{ padding: '6px 10px', borderTop: `1px solid ${darkMode ? '#333' : '#ddd'}`, fontSize: 10, color: darkMode ? '#888' : '#999' }}>
                Total: {segments.reduce((sum, s) => sum + (s.footage || s.length || 0), 0).toLocaleString()} LF
              </div>
            )}
          </div>
        )}
        {!itemsExpanded && (
          <button onClick={() => setItemsExpanded(true)} style={{
            position: 'absolute', bottom: 36, left: 10, padding: '6px 12px',
            background: darkMode ? 'rgba(26,26,46,0.95)' : 'rgba(255,255,255,0.95)',
            border: `1px solid ${darkMode ? '#333' : '#ddd'}`, borderRadius: 6,
            color: darkMode ? '#e0e0e0' : '#333', cursor: 'pointer', fontSize: 11, zIndex: 100,
          }}>Items ({segments.length + devices.length})</button>
        )}

        {/* === SUB-COMPONENT PANELS === */}
        {showStyleSettings && (
          <StyleSettingsPanel
            styleOverrides={styleOverrides}
            setStyleOverrides={setStyleOverrides}
            onClose={() => setShowStyleSettings(false)}
            darkMode={darkMode}
          />
        )}
        {showShortcutsModal && (
          <KeyboardShortcutsModal
            onClose={() => setShowShortcutsModal(false)}
            darkMode={darkMode}
          />
        )}
        {showBillingSummary && (
          <BillingSummaryPanel
            segments={segments}
            devices={devices}
            onClose={() => setShowBillingSummary(false)}
            darkMode={darkMode}
          />
        )}
        {showVersionSnapshots && (
          <VersionSnapshotsPanel
            segments={segments}
            devices={devices}
            onLoad={(data) => {
              if (data.segments) setSegments(data.segments);
              if (data.devices) setDevices(data.devices);
            }}
            onClose={() => setShowVersionSnapshots(false)}
            darkMode={darkMode}
          />
        )}
      </div>

      <style>{`@media (max-width: 768px) { .hide-mobile-label { display: none !important; } }`}</style>
    </div>
  );
}

// ============================================================
// SEGMENT DETAIL PANEL
// ============================================================
function SegmentPanel({ segment, segments, onUpdate, onDelete, onPullFiber }) {
  const [showPullFiber, setShowPullFiber] = React.useState(false);
  const [pullSide, setPullSide] = React.useState('top');
  const [fiberSlots, setFiberSlots] = React.useState([FIBER_SPECS[0].value]);
  const [quickFiberSpec, setQuickFiberSpec] = React.useState(FIBER_SPECS[4].value); // BFO48 Micro default
  const isConduit = segment.lineType === 'conduit';
  const childFibers = segments ? segments.filter(s => s.parentId === segment.id) : [];
  const topFibers = childFibers.filter(f => (f.fiberIndex || 0) >= 0);
  const bottomFibers = childFibers.filter(f => (f.fiberIndex || 0) < 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>{segment.id}</div>
        <button onClick={onDelete} style={{ ...btnStyle('#7f1d1d'), fontSize: 11 }}>Delete</button>
      </div>

      {/* Quick Add Cable buttons — conduit only */}
      {isConduit && (
        <div style={{ marginBottom: 10, background: 'rgba(255,255,255,0.03)', borderRadius: 6, padding: 8, border: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 6 }}>
            <select value={quickFiberSpec} onChange={e => setQuickFiberSpec(e.target.value)}
              style={{ background: '#0f172a', color: '#fff', border: `1px solid ${(FIBER_SPECS.find(s => s.value === quickFiberSpec) || {}).color || '#888'}`, borderRadius: 4, padding: '3px 6px', fontSize: 11, flex: 1 }}>
              {FIBER_SPECS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => onPullFiber([quickFiberSpec], 'top')}
              style={{ ...btnStyle('#1e3a5f'), flex: 1, fontSize: 11, padding: '6px 0' }}>
              + Top ({topFibers.length})
            </button>
            <button onClick={() => onPullFiber([quickFiberSpec], 'bottom')}
              style={{ ...btnStyle('#1e3a5f'), flex: 1, fontSize: 11, padding: '6px 0' }}>
              + Bottom ({bottomFibers.length})
            </button>
          </div>
        </div>
      )}

      {segment.parentId && (
        <div style={{ fontSize: 10, color: '#FF7F00', marginBottom: 8, padding: '3px 6px', background: 'rgba(255,127,0,0.1)', borderRadius: 4 }}>
          Pulled through {segment.parentId}
        </div>
      )}
      <div style={fieldStyle}>
        <label style={{ fontSize: 11, color: '#8892b0' }}>Footage (LF)</label>
        <input type="number" value={segment.footage || 0} onChange={e => onUpdate('footage', parseInt(e.target.value) || 0)} style={inputStyle} />
      </div>
      <div style={fieldStyle}>
        <label style={{ fontSize: 11, color: '#8892b0' }}>{segment.lineType === 'fiber' ? 'Fiber Type' : 'Conduit Spec'}</label>
        <select value={segment.conduitSpec} onChange={e => onUpdate('conduitSpec', e.target.value)} style={inputStyle}>
          {segment.lineType === 'fiber'
            ? FIBER_SPECS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)
            : CONDUIT_SPECS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)
          }
        </select>
      </div>
      <div style={fieldStyle}>
        <label style={{ fontSize: 11, color: '#8892b0' }}>Street</label>
        <input type="text" value={segment.street || ''} onChange={e => onUpdate('street', e.target.value)} placeholder="e.g., SARA ST" style={inputStyle} />
      </div>
      <div style={fieldStyle}>
        <label style={{ fontSize: 11, color: '#8892b0' }}>Notes</label>
        <textarea value={segment.notes || ''} onChange={e => onUpdate('notes', e.target.value)} placeholder="Add notes..." rows={3}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
      </div>

      {/* Pull Fiber — only for conduit segments */}
      {isConduit && (
        <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 10 }}>
          {childFibers.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: '#8892b0', marginBottom: 4 }}>Fiber in this conduit:</div>
              {childFibers.map(f => {
                const spec = FIBER_SPECS.find(s => s.value === f.conduitSpec);
                const side = (f.fiberIndex || 0) >= 0 ? 'T' : 'B';
                return (
                  <div key={f.id} style={{ fontSize: 11, padding: '2px 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: spec?.color || '#FF7F00', display: 'inline-block' }} />
                    <span style={{ color: '#64748b', fontSize: 9, fontWeight: 700 }}>{side}</span>
                    <span style={{ color: '#ccd6f6' }}>{f.id}</span>
                    <span style={{ color: '#8892b0' }}>{spec?.label || f.conduitSpec}</span>
                  </div>
                );
              })}
            </div>
          )}
          {!showPullFiber ? (
            <button onClick={() => setShowPullFiber(true)}
              style={{ ...btnStyle('#1e3a5f'), width: '100%', fontSize: 12, padding: '8px 0' }}>
              Pull Multiple ({childFibers.length} cables in conduit)
            </button>
          ) : (
            <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 6, padding: 10, border: '1px solid rgba(255,127,0,0.3)' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#FF7F00', marginBottom: 8 }}>Pull Fiber Cables</div>
              <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                <button onClick={() => setPullSide('top')} style={{ ...btnStyle(pullSide === 'top' ? '#166534' : '#334155'), flex: 1, fontSize: 11 }}>Top</button>
                <button onClick={() => setPullSide('bottom')} style={{ ...btnStyle(pullSide === 'bottom' ? '#166534' : '#334155'), flex: 1, fontSize: 11 }}>Bottom</button>
              </div>
              {fiberSlots.map((spec, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: '#8892b0', minWidth: 14 }}>#{i + 1}</span>
                  <select value={spec} onChange={e => {
                    const newSlots = [...fiberSlots];
                    newSlots[i] = e.target.value;
                    setFiberSlots(newSlots);
                  }} style={{ ...inputStyle, flex: 1, marginBottom: 0 }}>
                    {FIBER_SPECS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  {fiberSlots.length > 1 && (
                    <button onClick={() => setFiberSlots(prev => prev.filter((_, j) => j !== i))}
                      style={{ background: 'transparent', border: 'none', color: '#f44', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}>x</button>
                  )}
                </div>
              ))}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                {fiberSlots.length < 5 && (
                  <button onClick={() => setFiberSlots(prev => [...prev, FIBER_SPECS[0].value])}
                    style={{ ...btnStyle('#334155'), flex: 1, fontSize: 11 }}>+ Add Cable</button>
                )}
                <button onClick={() => { onPullFiber(fiberSlots, pullSide); setShowPullFiber(false); setFiberSlots([FIBER_SPECS[0].value]); }}
                  style={{ ...btnStyle('#166534'), flex: 1, fontSize: 11 }}>Pull {fiberSlots.length} Cable{fiberSlots.length > 1 ? 's' : ''}</button>
              </div>
              <button onClick={() => { setShowPullFiber(false); setFiberSlots([FIBER_SPECS[0].value]); }}
                style={{ background: 'transparent', border: 'none', color: '#8892b0', cursor: 'pointer', fontSize: 10, marginTop: 6, display: 'block', width: '100%', textAlign: 'center' }}>Cancel</button>
            </div>
          )}
        </div>
      )}

      <div style={{ fontSize: 11, color: '#555', marginTop: 8 }}>
        Vertices: {segment.points.length} | Measured: {polylineLength(segment.points)} LF
      </div>
      <div style={{ fontSize: 10, color: '#555', marginTop: 2 }}>
        Start: {segment.points[0].lat.toFixed(6)}, {segment.points[0].lng.toFixed(6)}
      </div>
      <div style={{ fontSize: 10, color: '#555' }}>
        End: {segment.points[segment.points.length - 1].lat.toFixed(6)}, {segment.points[segment.points.length - 1].lng.toFixed(6)}
      </div>
    </div>
  );
}

// ============================================================
// DEVICE DETAIL PANEL
// ============================================================
function DevicePanel({ device, onUpdate, onDelete }) {
  const typeLabels = { handhole: 'Handhole', flowerpot: 'Flowerpot', splitter_1x4: '1x4 Splitter', splitter_2x8: '1x8 Splitter', splice_point: 'Splice Point', slack_loop: 'Slack Loop', ground_rod: 'Ground Rod' };
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>{device.id}</div>
        <button onClick={onDelete} style={{ ...btnStyle('#7f1d1d'), fontSize: 11 }}>Delete</button>
      </div>
      <div style={fieldStyle}>
        <label style={{ fontSize: 11, color: '#8892b0' }}>Type</label>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{typeLabels[device.type] || device.type}</div>
      </div>
      {device.type === 'handhole' && (
        <div style={fieldStyle}>
          <label style={{ fontSize: 11, color: '#8892b0' }}>Size</label>
          <select value={device.subtype || '30x48x24'} onChange={e => onUpdate('subtype', e.target.value)} style={inputStyle}>
            {HANDHOLE_TYPES.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
          </select>
        </div>
      )}
      {(device.type === 'splice_point' || device.type === 'splitter_1x4' || device.type === 'splitter_2x8') && (
        <div style={fieldStyle}>
          <label style={{ fontSize: 11, color: '#8892b0' }}>Splice Type</label>
          <select value={device.subtype || 'ring_cut'} onChange={e => onUpdate('subtype', e.target.value)} style={inputStyle}>
            <option value="ring_cut">Ring Cut</option>
            <option value="1x4">1x4 Splitter</option>
            <option value="1x8">1x8 Splitter</option>
            <option value="f1">F1 / TYCO-D</option>
            <option value="ribbon">Ribbon Splice</option>
          </select>
        </div>
      )}
      {device.type === 'slack_loop' && (
        <>
          <div style={fieldStyle}>
            <label style={{ fontSize: 11, color: '#8892b0' }}>Slack Length</label>
            <select value={device.subtype || '30'} onChange={e => onUpdate('subtype', e.target.value)} style={inputStyle}>
              {SLACK_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={{ fontSize: 11, color: '#8892b0' }}>Rotation ({Math.round(device.rotation || 0)}°)</label>
            <input type="range" min={-180} max={180} step={1} value={device.rotation || 0}
              onChange={e => onUpdate('rotation', parseFloat(e.target.value))}
              style={{ width: '100%', accentColor: '#888' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
              <span style={{ fontSize: 9, color: '#555' }}>-180°</span>
              <button onClick={() => onUpdate('rotation', 0)}
                style={{ background: 'transparent', border: 'none', color: '#8892b0', cursor: 'pointer', fontSize: 10 }}>Reset</button>
              <span style={{ fontSize: 9, color: '#555' }}>180°</span>
            </div>
          </div>
        </>
      )}
      <div style={fieldStyle}>
        <label style={{ fontSize: 11, color: '#8892b0' }}>Notes</label>
        <textarea value={device.notes || ''} onChange={e => onUpdate('notes', e.target.value)} placeholder="Add notes..." rows={3}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
      </div>
      <div style={{ fontSize: 11, color: '#555', marginTop: 8 }}>
        GPS: {device.lat.toFixed(6)}, {device.lng.toFixed(6)}
      </div>
      <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>
        Drag the gold circle on the map to reposition.
      </div>
    </div>
  );
}

// ============================================================
// SHARED STYLES
// ============================================================
function btnStyle(bg) {
  return {
    background: bg || '#334155', color: '#fff', border: 'none', borderRadius: 6,
    padding: '5px 10px', fontSize: 12, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
  };
}

const zoomBtnStyle = {
  background: 'rgba(17,34,64,0.9)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: 6, width: 36, height: 36, fontSize: 18, fontWeight: 700, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)',
};

const fieldStyle = { marginBottom: 10 };
const inputStyle = {
  width: '100%', background: '#1a2744', color: '#fff',
  border: '1px solid rgba(255,255,255,0.15)', borderRadius: 4,
  padding: '6px 8px', fontSize: 13, marginTop: 2, boxSizing: 'border-box',
};

const toolbarGroupStyle = (dark) => ({
  background: dark ? 'rgba(26, 26, 46, 0.95)' : 'rgba(255, 255, 255, 0.95)',
  border: `1px solid ${dark ? '#333' : '#ddd'}`,
  borderRadius: 8,
  padding: '6px 8px',
  backdropFilter: 'blur(8px)',
});

const tgLabel = (dark) => ({
  fontSize: 9,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 1.5,
  color: dark ? '#888' : '#999',
  marginBottom: 4,
});

const tgSelect = (dark) => ({
  padding: '4px 6px',
  borderRadius: 4,
  border: `1px solid ${dark ? '#444' : '#ccc'}`,
  background: dark ? '#16213e' : '#f8f9fa',
  color: dark ? '#e0e0e0' : '#333',
  fontSize: 11,
  cursor: 'pointer',
  marginTop: 4,
  width: '100%',
});
